import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Tooltip,
  InputAdornment,
  Grid,
  TablePagination,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Stack,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  FileDownload as FileDownloadIcon,
  ShoppingCart as TrollyIcon,
  Inventory as InventoryIcon,
  PostAdd as PostAddIcon,
  Remove as RemoveIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  Tune as TuneIcon,
    SwapHoriz as TransferIcon,
  ViewModule as FloorLayoutIcon,
  LockOpen as LockOpenIcon,
    QrCode2 as QrCodeIcon,
} from '@mui/icons-material';
import { pfpTrollyStockService, pfpTrollyReleaseService } from '../services';
import pfpTrollyFloorLayoutService from '../services/pfpTrollyFloorLayout.service';
import { useAuthStore } from '../store/authStore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Html5Qrcode, CameraDevice } from 'html5-qrcode';

interface TrollyItem {
  trolly_number: string;
  quantity: number;
  location?: string;
}

interface PfpTrollyStock {
  id: number;
  batch_number: string;
  date_in: string;
  initial_weight: number;
  current_weight: number;
  pfp_type: 'Direct' | 'Stock' | 'Digi';
  color_name?: string;
  customer?: string;
  fabrication?: string;
  status: 'Active' | 'Depleted' | 'On Hold';
  created_by_name?: string;
  total_released?: number;
  release_count?: number;
  trolly_count?: number;
  trolly_number?: string;
  location?: string;
  created_at?: string;
}

interface PfpTrollyRelease {
  id: number;
  stock_id: number;
  batch_number: string;
  release_datetime: string;
  reference_number: string;
  p_batch?: string;
  releasing_quantity: number;
  trolly_items?: string; // JSON string
  remarks?: string;
  released_by_name?: string;
  stock_batch_number?: string;
  color_name?: string;
  customer?: string;
  pfp_type?: string;
}

interface Statistics {
  total_batches: number;
  active_batches: number;
  depleted_batches: number;
  total_initial_weight: number;
  total_current_weight: number;
  total_released_weight: number;
  low_stock_batches: number;
}

interface FloorSlot {
  type: 'empty' | 'occupied' | 'path' | 'blocked' | 'boundary';
  color: string;
  label: string;
  batchNumber?: string;
  trollyNumber?: string;
  qrCode?: string;
  slotQrCode?: string;
}

const DEFAULT_FLOOR_ROWS = 5;
const DEFAULT_FLOOR_COLS = 24;
const SLOT_COLORS = ['#4CAF50','#2196F3','#FF9800','#9C27B0','#F44336','#00BCD4','#FF5722','#795548','#E91E63','#607D8B'];
const DEFAULT_LAYOUT_NAME = 'Default Floor Layout';

const buildTrolleyQrCode = (batchNumber: string, trolleyNumber: string, itemId?: number) =>
  `PFP-TROLLEY|${String(batchNumber || '').trim()}|${String(trolleyNumber || '').trim()}|${itemId ?? Date.now().toString(36)}`;

const buildSlotQrCode = (_layoutName: string, rowIndex: number, colIndex: number, slotNumber?: string) =>
  String(slotNumber || `R${rowIndex + 1}C${colIndex + 1}`).trim();

const buildQrImageUrl = (qrValue: string, size = 220) => `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(qrValue)}`;

const makeEmptyFloor = (rows = DEFAULT_FLOOR_ROWS, cols = DEFAULT_FLOOR_COLS, layoutName = DEFAULT_LAYOUT_NAME): FloorSlot[][] =>
  Array.from({ length: rows }, (_, rowIndex) =>
    Array.from({ length: cols }, (_, colIndex) => {
      const slotQrCode = buildSlotQrCode(layoutName, rowIndex, colIndex);
      return { type: 'empty' as const, color: '#e0e0e0', label: '', qrCode: slotQrCode, slotQrCode };
    })
  );

const getRowLabel = (index: number): string => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let n = index;
  let label = '';
  do {
    label = alphabet[n % 26] + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
};

const PfpTrollyManagement: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const canEdit = user?.role === 'admin' || user?.role === 'manager';
  const canRelease = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'technician';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tabValue, setTabValue] = useState(0);
  const [stocks, setStocks] = useState<PfpTrollyStock[]>([]);
  const [stocksError, setStocksError] = useState<string | null>(null);
  const [releases, setReleases] = useState<PfpTrollyRelease[]>([]);
  const [activeBatches, setActiveBatches] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('Active');
  const [pfpTypeFilter, setPfpTypeFilter] = useState<string>('');
  const [colorFilter, setColorFilter] = useState<string>('');
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [fabricationFilter, setFabricationFilter] = useState<string>('');
  const [stockSort, setStockSort] = useState<string>(''); // '' | 'newest' | 'oldest' | 'location'
  const [lbsFilterTarget, setLbsFilterTarget] = useState<'batch' | 'trolley'>('batch');
  const [lbsMin, setLbsMin] = useState<string>('');
  const [lbsMax, setLbsMax] = useState<string>('');
  const [flatTrollyItems, setFlatTrollyItems] = useState<Array<{
    batch_number: string; date_in: string; color_name: string; customer: string;
    pfp_type: string; trolly_number: string; location: string; current_quantity: number;
  }>>([]);
  const [flatTrollyLoading, setFlatTrollyLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<PfpTrollyStock | null>(null);
  const [selectedRelease, setSelectedRelease] = useState<PfpTrollyRelease | null>(null);
  const [viewStock, setViewStock] = useState<PfpTrollyStock | null>(null);
  const [stockReleases, setStockReleases] = useState<PfpTrollyRelease[]>([]);
  const [viewTrollyItems, setViewTrollyItems] = useState<any[]>([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Stock form state
  const [stockForm, setStockForm] = useState({
    batch_number: '',
    date_in: new Date().toISOString().split('T')[0],
    pfp_type: 'Stock' as 'Direct' | 'Stock' | 'Digi',
    color_name: '',
    customer: '',
    fabrication: '',
  });
  
  // Trolly items for stock
  const [trollyItems, setTrollyItems] = useState<TrollyItem[]>([]);
  const [currentTrollyNumber, setCurrentTrollyNumber] = useState('');
  const [currentTrollyQuantity, setCurrentTrollyQuantity] = useState('');
  const [currentTrollyLocation, setCurrentTrollyLocation] = useState('');

  // Release form state
  const [releaseForm, setReleaseForm] = useState({
    stock_id: null as number | null,
    batch_number: '',
    release_datetime: new Date().toISOString().slice(0, 16),
    reference_number: '',
    p_batch: '',
    remarks: '',
  });
  
  // Available trollies and selected releases
  const [availableTrollies, setAvailableTrollies] = useState<any[]>([]);
  const [trollyReleases, setTrollyReleases] = useState<{trolly_number: string, quantity: number}[]>([]);
  const [isReturnMode, setIsReturnMode] = useState(false);
  const [returnDepartment, setReturnDepartment] = useState('');
  const [returnReason, setReturnReason] = useState('');

  // Manage trollies dialog state
  const [manageTrollyDialogOpen, setManageTrollyDialogOpen] = useState(false);
  const [manageTrollyBatch, setManageTrollyBatch] = useState<PfpTrollyStock | null>(null);
  const [manageTrollyItems, setManageTrollyItems] = useState<any[]>([]);
  const [manageTrollyLoading, setManageTrollyLoading] = useState(false);

  // Add returned trolly state
  const [addTrollyDialogOpen, setAddTrollyDialogOpen] = useState(false);
  const [addTrollyBatch, setAddTrollyBatch] = useState<PfpTrollyStock | null>(null);
  const [addTrollyForm, setAddTrollyForm] = useState({ trolly_number: '', quantity_kg: '', location: '', remarks: '' });

  // Edit trolly item state
  const [editTrollyDialogOpen, setEditTrollyDialogOpen] = useState(false);
  const [editTrollyItem, setEditTrollyItem] = useState<any>(null);
  const [editTrollyForm, setEditTrollyForm] = useState({ trolly_number: '', location: '' });

  // Transfer trolly load state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferSourceItem, setTransferSourceItem] = useState<any>(null);
  const [transferForm, setTransferForm] = useState({ to_trolly_number: '', transfer_quantity: '', transfer_all: false, is_new_trolly: false });

  // CSV upload state
  const [csvUploadDialogOpen, setCsvUploadDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvUploadLoading, setCsvUploadLoading] = useState(false);
  const [csvUploadResult, setCsvUploadResult] = useState<any>(null);
  const [excelPasteData, setExcelPasteData] = useState('');

  // Floor layout state
  const [layoutList, setLayoutList] = useState<Array<{ id: number; name: string; gridRows: number; gridCols: number }>>([]);
  const [currentLayoutId, setCurrentLayoutId] = useState<number | null>(null);
  const [currentLayoutName, setCurrentLayoutName] = useState(DEFAULT_LAYOUT_NAME);
  const [floorLayout, setFloorLayout] = useState<FloorSlot[][]>(makeEmptyFloor());
  const [floorEditMode, setFloorEditMode] = useState(false);
  const [selectedFloorSlot, setSelectedFloorSlot] = useState<{ row: number; col: number } | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());
  const [isDragSelecting, setIsDragSelecting] = useState(false);
  const [dragSelectionMode, setDragSelectionMode] = useState<'add' | 'remove'>('add');
  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [slotEditForm, setSlotEditForm] = useState<{ type: FloorSlot['type']; color: string; label: string; batchNumber: string; trollyNumber: string }>({
    type: 'empty', color: '#4CAF50', label: '', batchNumber: '', trollyNumber: '',
  });
  const [allTrollyItems, setAllTrollyItems] = useState<Array<{
    id: number;
    batch_number: string;
    trolly_number: string;
    location: string;
    location_qr_code?: string | null;
    qr_code?: string | null;
    initial_quantity: number;
    current_quantity: number;
    pfp_type?: string;
    date_in?: string;
    updated_at?: string;
  }>>([]);
  const [floorDataLoaded, setFloorDataLoaded] = useState(false);
  const [layoutSaving, setLayoutSaving] = useState(false);
  const [layoutLoading, setLayoutLoading] = useState(false);
  const [layoutNameDraft, setLayoutNameDraft] = useState(DEFAULT_LAYOUT_NAME);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTrolleyQr, setMergeTrolleyQr] = useState('');
  const [mergeSlotQr, setMergeSlotQr] = useState('');
  const [mergeScanError, setMergeScanError] = useState('');
  const [mergeAvailableCameras, setMergeAvailableCameras] = useState<CameraDevice[]>([]);
  const [mergeCameraIndex, setMergeCameraIndex] = useState(0);
  const [mergeScanMode, setMergeScanMode] = useState<'trolley' | 'slot' | null>(null);
  const mergeScannerRef = useRef<Html5Qrcode | null>(null);
  const [selectedSlotQr, setSelectedSlotQr] = useState<{ title: string; value: string } | null>(null);

  useEffect(() => {
    fetchData();
    fetchStatistics();
    fetchActiveBatches();
    loadFloorLayouts();
    return () => {
      stopMergeScanner();
    };
  }, []);

  useEffect(() => {
    if (tabValue === 0) {
      fetchStocks();
    } else if (tabValue === 1) {
      fetchReleases();
    } else if (tabValue === 2 && !floorDataLoaded) {
      loadAllTrollyItems();
    }
  }, [tabValue, searchTerm, statusFilter, pfpTypeFilter, colorFilter, customerFilter, fabricationFilter]);

  const fetchData = async () => {
    setFloorDataLoaded(false);
    await Promise.all([fetchStocks(), fetchReleases()]);
  };

  const fetchStocks = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (pfpTypeFilter) params.pfp_type = pfpTypeFilter;
      if (colorFilter) params.color_name = colorFilter;
      if (customerFilter) params.customer = customerFilter;
      if (fabricationFilter) params.fabrication = fabricationFilter;

      const data = await pfpTrollyStockService.getAll(params);
      if (!Array.isArray(data.stocks)) {
        throw new Error('The stock API response did not include a stocks list.');
      }
      setStocks(data.stocks);
      setStocksError(null);
    } catch (error: any) {
      console.error('Error fetching stocks:', error);
      setStocksError(error.response?.data?.error || error.message || 'Unable to load trolley stock data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchReleases = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (searchTerm) params.search = searchTerm;

      const data = await pfpTrollyReleaseService.getAll(params);
      setReleases(data.releases || []);
    } catch (error: any) {
      console.error('Error fetching releases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const data = await pfpTrollyStockService.getStatistics();
      setStatistics(data.stats);
    } catch (error: any) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchActiveBatches = async () => {
    try {
      const data = await pfpTrollyStockService.getActiveBatches();
      setActiveBatches(data.batches || []);
    } catch (error: any) {
      console.error('Error fetching active batches:', error);
    }
  };

  const handleStockDialogOpen = async (stock?: PfpTrollyStock) => {
    if (stock) {
      setSelectedStock(stock);
      setStockForm({
        batch_number: stock.batch_number,
        date_in: stock.date_in.split('T')[0], // Extract date part only (YYYY-MM-DD)
        pfp_type: stock.pfp_type,
        color_name: stock.color_name || '',
        customer: stock.customer || '',
        fabrication: stock.fabrication || '',
      });
      
      // Fetch trolly items for this batch when editing
      try {
        const response = await pfpTrollyStockService.getTrollyItems(stock.batch_number);
        const trollyData = response.trollyItems || [];
        setTrollyItems(trollyData.map((item: any) => ({
          trolly_number: item.trolly_number,
          quantity: parseFloat(item.initial_quantity),
          location: item.location || undefined
        })));
      } catch (error) {
        console.error('Error fetching trolly items:', error);
        setTrollyItems([]);
      }
    } else {
      setSelectedStock(null);
      setStockForm({
        batch_number: '',
        date_in: new Date().toISOString().split('T')[0],
        pfp_type: 'Stock',
        color_name: '',
        customer: '',
        fabrication: '',
      });
      setTrollyItems([]);
      setCurrentTrollyNumber('');
      setCurrentTrollyQuantity('');
    }
    setStockDialogOpen(true);
    setSaveError(null);
  };

  const handleStockDialogClose = () => {
    setStockDialogOpen(false);
    setSelectedStock(null);
    setTrollyItems([]);
    setCurrentTrollyNumber('');
    setCurrentTrollyQuantity('');
    setSaveError(null);
  };

  const handleAddTrolly = () => {
    if (!currentTrollyNumber.trim() || !currentTrollyQuantity || parseFloat(currentTrollyQuantity) <= 0) {
      setSaveError('Please enter valid trolly number and quantity');
      return;
    }

    // Check if trolly number already exists
    if (trollyItems.find(item => item.trolly_number === currentTrollyNumber.trim())) {
      setSaveError('Trolly number already added');
      return;
    }

    // Convert KG to LBS (1 KG = 2.20462 LBS)
    const quantityInKg = parseFloat(currentTrollyQuantity);
    const quantityInLbs = quantityInKg * 2.20462;

    setTrollyItems([...trollyItems, {
      trolly_number: currentTrollyNumber.trim(),
      quantity: quantityInLbs,
      location: currentTrollyLocation.trim() || undefined
    }]);
    setCurrentTrollyNumber('');
    setCurrentTrollyQuantity('');
    setCurrentTrollyLocation('');
    setSaveError(null);
  };

  const handleRemoveTrolly = (trollyNumber: string) => {
    setTrollyItems(trollyItems.filter(item => item.trolly_number !== trollyNumber));
  };

  const getTotalWeight = () => {
    return trollyItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  const handleStockSubmit = async () => {
    try {
      setSaveLoading(true);
      setSaveError(null);

      if (trollyItems.length === 0) {
        setSaveError('Please add at least one trolly');
        setSaveLoading(false);
        return;
      }

      const data = {
        ...stockForm,
        trolly_items: trollyItems,
      };

      if (selectedStock) {
        await pfpTrollyStockService.update(selectedStock.id, data);
      } else {
        await pfpTrollyStockService.create(data);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      handleStockDialogClose();
      fetchData();
      fetchStatistics();
      fetchActiveBatches();
    } catch (error: any) {
      console.error('Error saving stock:', error);
      setSaveError(error.response?.data?.error || 'Failed to save stock');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleReleaseDialogOpen = async (release?: PfpTrollyRelease) => {
    if (release) {
      setSelectedRelease(release);
      setReleaseForm({
        stock_id: release.stock_id,
        batch_number: release.batch_number,
        release_datetime: release.release_datetime.slice(0, 16),
        reference_number: release.reference_number,
        p_batch: release.p_batch || '',
        remarks: release.remarks || '',
      });
      
      // Parse and load existing trolly releases
      try {
        if (release.trolly_items) {
          const trollyItems = JSON.parse(release.trolly_items);
          setTrollyReleases(trollyItems.map((item: any) => ({
            trolly_number: item.trolly_number,
            quantity: parseFloat(item.quantity)
          })));
        } else {
          setTrollyReleases([]);
        }
        
        // Load available trollies for the batch
        const response = await pfpTrollyStockService.getAvailableTrollies(release.batch_number);
        setAvailableTrollies(response.trollies || []);
      } catch (error) {
        console.error('Error loading release trolly items:', error);
        setTrollyReleases([]);
        setAvailableTrollies([]);
      }
    } else {
      setSelectedRelease(null);
      setReleaseForm({
        stock_id: null,
        batch_number: '',
        release_datetime: new Date().toISOString().slice(0, 16),
        reference_number: '',
        p_batch: '',
        remarks: '',
      });
      setAvailableTrollies([]);
      setTrollyReleases([]);
    }
    setReleaseDialogOpen(true);
    setSaveError(null);
  };

  const handleReleaseDialogClose = () => {
    setReleaseDialogOpen(false);
    setSelectedRelease(null);
    setAvailableTrollies([]);
    setTrollyReleases([]);
    setSaveError(null);
  };

  const handleBatchSelect = async (batchNumber: string) => {
    if (!batchNumber) {
      setAvailableTrollies([]);
      setTrollyReleases([]);
      return;
    }

    try {
      const response = await pfpTrollyStockService.getAvailableTrollies(batchNumber);
      setAvailableTrollies(response.trollies || []);
      setTrollyReleases([]);
    } catch (error) {
      console.error('Error fetching available trollies:', error);
      setSaveError('Failed to load available trollies');
    }
  };

  const handleTrollyReleaseQuantityChange = (trollyNumber: string, quantity: string) => {
    const qty = parseFloat(quantity) || 0;
    const existing = trollyReleases.find(t => t.trolly_number === trollyNumber);
    
    if (qty <= 0) {
      setTrollyReleases(trollyReleases.filter(t => t.trolly_number !== trollyNumber));
    } else if (existing) {
      setTrollyReleases(trollyReleases.map(t => 
        t.trolly_number === trollyNumber ? { ...t, quantity: qty } : t
      ));
    } else {
      setTrollyReleases([...trollyReleases, { trolly_number: trollyNumber, quantity: qty }]);
    }
  };

  const getTotalReleasing = () => {
    return trollyReleases.reduce((sum, item) => sum + item.quantity, 0);
  };

  const handleOpenManageTrollies = async (stock: PfpTrollyStock) => {
    setManageTrollyBatch(stock);
    setManageTrollyItems([]);
    setManageTrollyLoading(true);
    setManageTrollyDialogOpen(true);
    try {
      const data = await pfpTrollyStockService.getTrollyItems(stock.batch_number);
      setManageTrollyItems(data.trollyItems || []);
    } catch (error) {
      console.error('Error fetching trolly items:', error);
    } finally {
      setManageTrollyLoading(false);
    }
  };

  const handleOpenManageTrolliesByBatch = async (batchNumber: string) => {
    setManageTrollyItems([]);
    setManageTrollyLoading(true);
    setManageTrollyDialogOpen(true);
    try {
      const stockData = await pfpTrollyStockService.getByBatchNumber(batchNumber);
      setManageTrollyBatch(stockData.stock);
      const trollyData = await pfpTrollyStockService.getTrollyItems(batchNumber);
      setManageTrollyItems(trollyData.trollyItems || []);
    } catch (error) {
      console.error('Error fetching trolly items:', error);
    } finally {
      setManageTrollyLoading(false);
    }
  };

  const refreshManageTrollyItems = async (batchNumber: string) => {
    const data = await pfpTrollyStockService.getTrollyItems(batchNumber);
    setManageTrollyItems(data.trollyItems || []);
  };

  const handleOpenTransfer = (item: any) => {
    setTransferSourceItem(item);
    setTransferForm({ to_trolly_number: '', transfer_quantity: '', transfer_all: false, is_new_trolly: false });
    setSaveError(null);
    setTransferDialogOpen(true);
  };

  const handleTransferSubmit = async () => {
    if (!transferSourceItem || !manageTrollyBatch) return;
    const qty = transferForm.transfer_all
      ? parseFloat(transferSourceItem.current_quantity)
      : parseFloat(transferForm.transfer_quantity);
    if (!transferForm.to_trolly_number.trim()) {
      setSaveError('Destination trolly number is required');
      return;
    }
    if (isNaN(qty) || qty <= 0) {
      setSaveError('Transfer quantity must be a positive number');
      return;
    }
    if (qty > parseFloat(transferSourceItem.current_quantity)) {
      setSaveError(`Cannot transfer more than available (${parseFloat(transferSourceItem.current_quantity).toFixed(2)} LBS)`);
      return;
    }
    try {
      setSaveLoading(true);
      setSaveError(null);
      await pfpTrollyStockService.transferTrollyLoad(manageTrollyBatch.batch_number, {
        from_trolly_number: transferSourceItem.trolly_number,
        to_trolly_number: transferForm.to_trolly_number.trim().toUpperCase(),
        transfer_quantity: qty,
      });
      setTransferDialogOpen(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      // Refresh manage dialog and batch info
      await refreshManageTrollyItems(manageTrollyBatch.batch_number);
      const stockData = await pfpTrollyStockService.getByBatchNumber(manageTrollyBatch.batch_number);
      setManageTrollyBatch(stockData.stock);
      // Refresh details dialog if open on same batch
      if (viewStock && viewStock.batch_number === manageTrollyBatch.batch_number) {
        const trollyData = await pfpTrollyStockService.getTrollyItems(manageTrollyBatch.batch_number);
        setViewTrollyItems(trollyData.trollyItems || []);
      }
      fetchData();
    } catch (error: any) {
      setSaveError(error.response?.data?.error || 'Failed to transfer trolly load');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleOpenAddTrolly = (stock: PfpTrollyStock) => {
    setAddTrollyBatch(stock);
    setAddTrollyForm({ trolly_number: '', quantity_kg: '', location: '', remarks: '' });
    setSaveError(null);
    setAddTrollyDialogOpen(true);
  };

  const handleAddTrollySubmit = async () => {
    if (!addTrollyBatch) return;
    if (!addTrollyForm.trolly_number.trim() || !addTrollyForm.quantity_kg || parseFloat(addTrollyForm.quantity_kg) <= 0) {
      setSaveError('Trolly number and a positive quantity are required');
      return;
    }
    try {
      setSaveLoading(true);
      setSaveError(null);
      await pfpTrollyStockService.addTrollyToBatch(addTrollyBatch.batch_number, {
        trolly_number: addTrollyForm.trolly_number.trim().toUpperCase(),
        quantity_kg: parseFloat(addTrollyForm.quantity_kg),
        location: addTrollyForm.location.trim() || undefined,
        remarks: addTrollyForm.remarks.trim() || undefined,
      });
      setAddTrollyDialogOpen(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      // Refresh open dialogs
      if (viewStock && viewStock.batch_number === addTrollyBatch.batch_number) {
        const trollyData = await pfpTrollyStockService.getTrollyItems(addTrollyBatch.batch_number);
        setViewTrollyItems(trollyData.trollyItems || []);
        const stockData = await pfpTrollyStockService.getByBatchNumber(addTrollyBatch.batch_number);
        setViewStock(stockData.stock);
      }
      if (manageTrollyDialogOpen && manageTrollyBatch && manageTrollyBatch.batch_number === addTrollyBatch.batch_number) {
        await refreshManageTrollyItems(addTrollyBatch.batch_number);
        // Also refresh batch weight shown in dialog title
        const stockData = await pfpTrollyStockService.getByBatchNumber(addTrollyBatch.batch_number);
        setManageTrollyBatch(stockData.stock);
      }
      fetchData();
      fetchStatistics();
      fetchActiveBatches();
    } catch (error: any) {
      setSaveError(error.response?.data?.error || 'Failed to add trolly');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleOpenEditTrolly = (item: any) => {
    setEditTrollyItem(item);
    setEditTrollyForm({ trolly_number: item.trolly_number, location: item.location || '' });
    setSaveError(null);
    setEditTrollyDialogOpen(true);
  };

  const handleEditTrollySubmit = async () => {
    if (!editTrollyItem) return;
    if (!editTrollyForm.trolly_number.trim()) {
      setSaveError('Trolly number is required');
      return;
    }
    try {
      setSaveLoading(true);
      setSaveError(null);
      await pfpTrollyStockService.updateTrollyItem(editTrollyItem.id, {
        trolly_number: editTrollyForm.trolly_number.trim().toUpperCase(),
        location: editTrollyForm.location.trim() || undefined,
      });
      setEditTrollyDialogOpen(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      // Refresh details dialog trolly list
      if (viewStock) {
        const trollyData = await pfpTrollyStockService.getTrollyItems(viewStock.batch_number);
        setViewTrollyItems(trollyData.trollyItems || []);
      }
      // Refresh manage trollies dialog
      if (manageTrollyDialogOpen && manageTrollyBatch && editTrollyItem) {
        await refreshManageTrollyItems(editTrollyItem.batch_number);
      }
    } catch (error: any) {
      setSaveError(error.response?.data?.error || 'Failed to update trolly item');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      setSaveError('Please select a CSV file');
      return;
    }

    try {
      setCsvUploadLoading(true);
      setSaveError(null);

      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await pfpTrollyStockService.uploadFabricationCSV(formData);
      setCsvUploadResult(response);
      
      // Refresh data
      fetchData();
      fetchStatistics();
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      console.error('Error uploading CSV:', error);
      setSaveError(error.response?.data?.error || 'Failed to upload CSV');
    } finally {
      setCsvUploadLoading(false);
    }
  };

  const handleCopyBatchNumbers = async () => {
    try {
      const batchNumbers = stocks.map(stock => stock.batch_number).join('\n');
      await navigator.clipboard.writeText(batchNumbers);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Error copying batch numbers:', error);
      setSaveError('Failed to copy batch numbers to clipboard');
    }
  };

  const handleParseExcelData = async () => {
    try {
      if (!excelPasteData.trim()) {
        setSaveError('Please paste data from Excel first');
        return;
      }

      setCsvUploadLoading(true);
      setSaveError(null);

      // Parse Excel data (tab or comma separated)
      const lines = excelPasteData.trim().split('\n');
      let csvContent = '';
      
      // Check if first line looks like headers
      const firstLine = lines[0];
      const hasHeaders = firstLine.toLowerCase().includes('batch') || 
                        firstLine.toLowerCase().includes('fabrication') ||
                        firstLine.toLowerCase().includes('color') ||
                        firstLine.toLowerCase().includes('customer');
      
      if (!hasHeaders) {
        // Add default headers if not present
        csvContent = 'batch_number,fabrication,color_name,customer\n';
      }
      
      // Convert each line to CSV format
      lines.forEach(line => {
        // Split by tab (Excel default) or comma
        const values = line.includes('\t') ? line.split('\t') : line.split(',');
        // Escape quotes and wrap in quotes if contains comma
        const escapedValues = values.map(val => {
          const trimmed = val.trim();
          if (trimmed.includes(',') || trimmed.includes('"')) {
            return `"${trimmed.replace(/"/g, '""')}"`;
          }
          return trimmed;
        });
        csvContent += escapedValues.join(',') + '\n';
      });

      // Create a File object from the CSV content
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const file = new File([blob], 'excel-paste-data.csv', { type: 'text/csv' });
      
      // Automatically upload the parsed data
      const formData = new FormData();
      formData.append('file', file);

      const response = await pfpTrollyStockService.uploadFabricationCSV(formData);
      setCsvUploadResult(response);
      
      // Refresh data
      fetchData();
      fetchStatistics();
      
      setExcelPasteData('');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      console.error('Error parsing/uploading Excel data:', error);
      setSaveError(error.response?.data?.error || 'Failed to parse and upload Excel data. Please check the format.');
    } finally {
      setCsvUploadLoading(false);
    }
  };

  const handleCsvDialogClose = () => {
    setCsvUploadDialogOpen(false);
    setCsvFile(null);
    setCsvUploadResult(null);
    setExcelPasteData('');
    setSaveError(null);
  };

  // ── Floor Layout functions ────────────────────────────────────
  const loadAllTrollyItems = async () => {
    try {
      const data = await pfpTrollyStockService.getAll({});
      const batches: any[] = data.stocks || [];
      const items: Array<{
        id: number;
        batch_number: string;
        trolly_number: string;
        location: string;
        location_qr_code?: string | null;
        qr_code?: string | null;
        initial_quantity: number;
        current_quantity: number;
        pfp_type?: string;
        color_name?: string;
        customer?: string;
        fabrication?: string;
        date_in?: string;
        updated_at?: string;
      }> = [];
      await Promise.all(
        batches.map(async (batch: any) => {
          try {
            const trollyResponse = await pfpTrollyStockService.getTrollyItems(batch.batch_number);
            const trollies = Array.isArray(trollyResponse)
              ? trollyResponse
              : (trollyResponse?.trollyItems || []);

            trollies.forEach((item: any) => {
              if (item.location || item.location_qr_code) {
                items.push({
                  id: item.id,
                  batch_number: batch.batch_number,
                  trolly_number: item.trolly_number,
                  location: (item.location || '').trim().toUpperCase(),
                  location_qr_code: item.location_qr_code || null,
                  qr_code: item.qr_code || null,
                  initial_quantity: Number(item.initial_quantity || item.quantity || 0),
                  current_quantity: Number(item.current_quantity),
                  pfp_type: batch.pfp_type,
                  color_name: batch.color_name,
                  customer: batch.customer,
                  fabrication: batch.fabrication,
                  date_in: batch.date_in,
                  updated_at: item.updated_at || item.created_at || new Date().toISOString(),
                });
              }
            });
          } catch {}
        })
      );

      const latestByLocation = new Map<string, any>();
      items.forEach((item) => {
        const key = item.location_qr_code || (item.location || '').trim().toUpperCase();
        if (!key) return;
        const existing = latestByLocation.get(key);
        if (!existing || new Date(item.updated_at || 0).getTime() >= new Date(existing.updated_at || 0).getTime()) {
          latestByLocation.set(key, item);
        }
      });

      setAllTrollyItems(Array.from(latestByLocation.values()));
      setFloorDataLoaded(true);
    } catch {}
  };

  const normalizeFloorCells = (cells: any[][], rows: number, cols: number, layoutName = currentLayoutName || DEFAULT_LAYOUT_NAME): FloorSlot[][] => {
    const base = makeEmptyFloor(rows, cols, layoutName);
    return base.map((row, rowIdx) =>
      row.map((slot, colIdx) => {
        const source = cells?.[rowIdx]?.[colIdx];
        if (!source) return slot;
        const slotQrCode = buildSlotQrCode(layoutName, rowIdx, colIdx, source.label || source.trollyNumber);
        return {
          type: source.type || 'empty',
          color: source.color || '#e0e0e0',
          label: source.label || '',
          batchNumber: source.batchNumber,
          trollyNumber: source.trollyNumber,
          qrCode: slotQrCode,
          slotQrCode,
        };
      })
    );
  };

  const stopMergeScanner = async () => {
    if (mergeScannerRef.current) {
      try {
        await mergeScannerRef.current.stop();
        mergeScannerRef.current.clear();
      } catch (_) {
        // no-op
      }
      mergeScannerRef.current = null;
    }
    setMergeScanMode(null);
  };

  const openMergeDialog = () => {
    setMergeTrolleyQr('');
    setMergeSlotQr('');
    setMergeScanError('');
    setMergeDialogOpen(true);
  };

  const startMergeScanner = async (mode: 'trolley' | 'slot') => {
    try {
      setMergeScanError('');
      setMergeScanMode(mode);

      const devices = await Html5Qrcode.getCameras();
      if (!devices || devices.length === 0) {
        throw new Error('No camera access available on this device');
      }

      setMergeAvailableCameras(devices);
      const html5QrCode = new Html5Qrcode('merge-qr-reader');
      mergeScannerRef.current = html5QrCode;

      await html5QrCode.start(
        devices[mergeCameraIndex]?.id || { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 220, height: 220 }, aspectRatio: 1.0 },
        (decodedText) => {
          if (mode === 'trolley') {
            setMergeTrolleyQr(decodedText);
          } else {
            setMergeSlotQr(decodedText);
          }
          stopMergeScanner();
        },
        () => undefined
      );
    } catch (error: any) {
      setMergeScanError(error.message || 'Failed to start camera scan');
      setMergeScanMode(null);
    }
  };

  const handleMergeQrAssignment = async () => {
    if (!mergeTrolleyQr.trim() || !mergeSlotQr.trim()) {
      setMergeScanError('Both trolley QR and slot QR are required.');
      return;
    }

    try {
      setSaveLoading(true);
      setMergeScanError('');

      const slotLabel = mergeSlotQr.trim();
      const trolleyLabel = mergeTrolleyQr.trim();

      await pfpTrollyStockService.linkTrolleyToLocation({
        trolley_qr_code: trolleyLabel,
        slot_qr_code: slotLabel,
      });

      setMergeDialogOpen(false);
      setMergeTrolleyQr('');
      setMergeSlotQr('');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);

      await loadAllTrollyItems();
    } catch (error: any) {
      setMergeScanError(error.response?.data?.error || 'Failed to connect trolley to slot');
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePrintSlotQr = (value: string, title: string) => {
    const printWindow = window.open('', '_blank', 'width=900,height=760');
    if (!printWindow) {
      setSaveError('Popup blocked. Please allow popups to print the slot QR code.');
      return;
    }
    printWindow.document.write(`<!doctype html><html><head><title>${title}</title><style>@page{size:A4;margin:15mm}body{font-family:Arial,sans-serif;text-align:center;padding-top:25mm}.qr{width:100mm;height:100mm}.title{font-size:28px;font-weight:bold;margin-bottom:12mm}</style></head><body><div class="title">${title}</div><img class="qr" src="${buildQrImageUrl(value, 700)}" alt="Floor slot QR code"><p>${value}</p></body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  const escapeHtml = (value: any) => String(value ?? '').replace(/[&<>"']/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[character] || character));

  const handlePrintTrolleySheet = (item: any) => {
    const qrValue = item.qr_code || buildTrolleyQrCode(item.batch_number, item.trolly_number, item.id);
    const safe = (value: any) => escapeHtml(value);

    const printWindow = window.open('', '_blank', 'width=900,height=760');
    if (!printWindow) {
      setSaveError('Popup blocked. Please allow popups to print the trolley sheet.');
      return;
    }

    const html = `
      <html>
        <head>
          <title>Trolley Sheet</title>
          <style>
            @page { size: A4 landscape; margin: 7mm; }
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 0; color: #111; }
            .sheet { width: 283mm; height: 196mm; margin: 0 auto; border: 1.5px solid #111; padding: 5mm; display: flex; flex-direction: column; }
            .main { display: grid; grid-template-columns: 2fr 1fr; gap: 5mm; flex: 1; min-height: 0; }
            .write-form { border-collapse: collapse; width: 100%; height: 100%; table-layout: fixed; }
            .write-form td { border: 1px solid #333; padding: 3mm; vertical-align: middle; }
            .write-form .label { width: 27%; font-size: 17pt; font-weight: 700; }
            .write-form .blank { height: 23mm; }
            .write-form .trolley-head { height: 18mm; text-align: center; font-size: 17pt; font-weight: 700; }
            .write-form .trolley-blank { height: 20mm; }
            .write-form .date-row { height: 30mm; }
            .write-form .remarks { height: 30mm; vertical-align: top; padding-top: 5mm; }
            .qr-panel { border: 1px solid #333; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 5mm; text-align: center; min-width: 0; }
            .title { font-size: 18pt; font-weight: 700; margin: 0 0 4mm; }
            .sub { font-size: 10pt; color: #444; margin-bottom: 4mm; }
            .qr { width: 76mm; height: 76mm; object-fit: contain; }
            .qr-caption { margin-top: 3mm; font-size: 9pt; overflow-wrap: anywhere; }
            .details { display: grid; grid-template-columns: repeat(5, 1fr); border: 1px solid #333; margin-top: 4mm; min-height: 24mm; }
            .detail { border-right: 1px solid #333; padding: 2mm 3mm; }
            .detail:last-child { border-right: 0; }
            .detail-label { display: block; font-size: 8pt; font-weight: 700; text-transform: uppercase; color: #444; margin-bottom: 1mm; }
            .detail-value { font-size: 10pt; font-weight: 600; overflow-wrap: anywhere; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <div class="sheet">
            <div class="main">
              <table class="write-form">
                <tr><td class="label">Batch No:</td><td class="blank" colspan="5">${safe(item.batch_number || '')}</td></tr>
                <tr>
                  <td class="label" rowspan="2">Trolley No:</td>
                  <td class="trolley-head">F</td><td class="trolley-head">P</td><td class="trolley-head">FLY</td><td class="trolley-head" colspan="2"></td>
                </tr>
                <tr><td class="trolley-blank"></td><td class="trolley-blank"></td><td class="trolley-blank"></td><td class="trolley-blank" colspan="2">${safe(item.trolly_number || '')}</td></tr>
                <tr><td class="label">LBS</td><td class="blank" colspan="5">${Number(item.current_quantity || 0).toFixed(2)}</td></tr>
                <tr><td class="label">Date</td><td class="date-row" colspan="2"></td><td colspan="3">2026 &nbsp;&nbsp;&nbsp; Day &nbsp;&nbsp;&nbsp; Night</td></tr>
                <tr><td class="label">Remarks</td><td class="remarks" colspan="5"></td></tr>
              </table>
              <div class="qr-panel">
                <p class="title">PFP TROLLEY</p>
                <div class="sub">Ocean Lanka Printing</div>
                <img class="qr" src="${buildQrImageUrl(qrValue, 800)}" alt="Trolley QR Code" />
                <div class="qr-caption">${safe(qrValue)}</div>
              </div>
            </div>
            <div class="details">
              <div class="detail"><span class="detail-label">PFP Type</span><span class="detail-value">${safe(item.pfp_type || '-')}</span></div>
              <div class="detail"><span class="detail-label">Color</span><span class="detail-value">${safe(item.color_name || '-')}</span></div>
              <div class="detail"><span class="detail-label">Customer</span><span class="detail-value">${safe(item.customer || '-')}</span></div>
              <div class="detail"><span class="detail-label">Fabrication</span><span class="detail-value">${safe(item.fabrication || '-')}</span></div>
              <div class="detail"><span class="detail-label">Location / Status</span><span class="detail-value">${safe(item.location || 'Not assigned')} / ${safe(item.status || 'Active')}</span></div>
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  const loadFloorLayouts = async () => {
    setLayoutLoading(true);
    try {
      const response = await pfpTrollyFloorLayoutService.listLayouts();
      const layouts = response.layouts || [];
      setLayoutList(layouts);

      if (layouts.length > 0) {
        const defaultLayout = layouts[0];
        const current = currentLayoutId
          ? layouts.find((layout: any) => layout.id === currentLayoutId) || defaultLayout
          : defaultLayout;

        const layoutResponse = await pfpTrollyFloorLayoutService.getLayout({ id: current.id });
        const floorLayoutData = layoutResponse.floorLayout;
        setCurrentLayoutId(current.id);
        setCurrentLayoutName(current.name);
        setLayoutNameDraft(current.name);
        setFloorLayout(normalizeFloorCells(floorLayoutData?.cells || [], floorLayoutData?.gridRows || current.gridRows || DEFAULT_FLOOR_ROWS, floorLayoutData?.gridCols || current.gridCols || DEFAULT_FLOOR_COLS, current.name));
      } else {
        setCurrentLayoutId(null);
        setCurrentLayoutName(DEFAULT_LAYOUT_NAME);
        setLayoutNameDraft(DEFAULT_LAYOUT_NAME);
        setFloorLayout(makeEmptyFloor(DEFAULT_FLOOR_ROWS, DEFAULT_FLOOR_COLS, DEFAULT_LAYOUT_NAME));
      }
    } catch (error) {
      console.error('Error loading floor layouts:', error);
      setFloorLayout(makeEmptyFloor());
    } finally {
      setLayoutLoading(false);
    }
  };

  const saveFloorLocally = (layout: FloorSlot[][]) => {
    try {
      localStorage.setItem(`pfp_floor_layout_draft_${currentLayoutName || DEFAULT_LAYOUT_NAME}`, JSON.stringify({
        rows: layout.length,
        cols: layout[0]?.length || 0,
        cells: layout,
      }));
    } catch {}
  };

  const persistCurrentLayout = async (overrideName?: string) => {
    if (!currentLayoutName && !overrideName) return;
    setLayoutSaving(true);
    try {
      const layoutName = overrideName || currentLayoutName;
      const cells = floorLayout.map((row, rowIdx) => row.map((slot, colIdx) => {
        const slotQrCode = buildSlotQrCode(layoutName, rowIdx, colIdx, slot.label || slot.trollyNumber);
        return { ...slot, qrCode: slotQrCode, slotQrCode };
      }));
      setFloorLayout(cells);
      await pfpTrollyFloorLayoutService.saveLayout({
        id: currentLayoutId || undefined,
        name: layoutName,
        gridRows: cells.length,
        gridCols: cells[0]?.length || 0,
        slots: [],
        cells,
      });
      await loadFloorLayouts();
    } finally {
      setLayoutSaving(false);
    }
  };

  const handleLayoutSelect = async (layoutName: string) => {
    setCurrentLayoutName(layoutName);
    setLayoutNameDraft(layoutName);
    const layout = layoutList.find(item => item.name === layoutName);
    if (!layout) {
      setCurrentLayoutId(null);
      setFloorLayout(makeEmptyFloor(DEFAULT_FLOOR_ROWS, DEFAULT_FLOOR_COLS, layoutName));
      return;
    }

    setLayoutLoading(true);
    try {
      const response = await pfpTrollyFloorLayoutService.getLayout({ id: layout.id });
      const floorLayoutData = response.floorLayout;
      setCurrentLayoutId(layout.id);
      setFloorLayout(normalizeFloorCells(floorLayoutData?.cells || [], floorLayoutData?.gridRows || layout.gridRows || DEFAULT_FLOOR_ROWS, floorLayoutData?.gridCols || layout.gridCols || DEFAULT_FLOOR_COLS, layout.name));
    } catch (error) {
      console.error('Error loading selected layout:', error);
      setFloorLayout(makeEmptyFloor(layout.gridRows, layout.gridCols));
    } finally {
      setLayoutLoading(false);
    }
  };

  const handleSaveLayout = async () => {
    const nextName = (layoutNameDraft || '').trim();
    if (!nextName) {
      setSaveError('Location name is required');
      return;
    }
    setSaveError(null);
    await persistCurrentLayout(nextName);
  };

  const handleDeleteLayout = async () => {
    if (!currentLayoutId) return;
    if (!window.confirm(`Delete layout "${currentLayoutName}"?`)) return;
    setLayoutSaving(true);
    try {
      await pfpTrollyFloorLayoutService.deleteLayout(currentLayoutId);
      setCurrentLayoutId(null);
      setCurrentLayoutName(DEFAULT_LAYOUT_NAME);
      setLayoutNameDraft(DEFAULT_LAYOUT_NAME);
      await loadFloorLayouts();
    } finally {
      setLayoutSaving(false);
    }
  };

  const handleSlotClick = (row: number, col: number) => {
    if (!floorEditMode) return;
    const key = `${row}-${col}`;
    setSelectedSlots(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSlotMouseDown = (row: number, col: number) => {
    if (!floorEditMode) return;
    const key = `${row}-${col}`;
    const alreadySelected = selectedSlots.has(key);
    setDragSelectionMode(alreadySelected ? 'remove' : 'add');
    setIsDragSelecting(true);
    setSelectedSlots(prev => {
      const next = new Set(prev);
      if (alreadySelected) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSlotMouseEnter = (row: number, col: number) => {
    if (!floorEditMode || !isDragSelecting) return;
    const key = `${row}-${col}`;
    setSelectedSlots(prev => {
      const next = new Set(prev);
      if (dragSelectionMode === 'add') next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const applyDemarcationToSelected = (type: FloorSlot['type'], colorOverride?: string) => {
    if (selectedSlots.size === 0) return;
    const defaultColor = colorOverride ||
      (type === 'path' ? '#FDD835' :
       type === 'blocked' ? '#9E9E9E' :
       type === 'boundary' ? '#7B1FA2' :
       type === 'empty' ? '#e0e0e0' : '#4CAF50');

    const newLayout = floorLayout.map((r, ri) =>
      r.map((slot, ci) => {
        const key = `${ri}-${ci}`;
        if (!selectedSlots.has(key)) return slot;
        const nextQrCode = buildSlotQrCode(currentLayoutName || DEFAULT_LAYOUT_NAME, ri, ci, slot.label || slot.trollyNumber);
        return {
          ...slot,
          type,
          color: defaultColor,
          label: type === 'empty' ? '' : slot.label,
          batchNumber: type === 'occupied' ? slot.batchNumber : undefined,
          trollyNumber: type === 'occupied' ? slot.trollyNumber : undefined,
          qrCode: nextQrCode,
          slotQrCode: nextQrCode,
        };
      })
    ) as FloorSlot[][];
    setFloorLayout(newLayout);
    saveFloorLocally(newLayout);
  };

  const handleAddRow = () => {
    const cols = floorLayout[0]?.length || DEFAULT_FLOOR_COLS;
    const rowIndex = floorLayout.length;
    const newLayout = [...floorLayout, Array.from({ length: cols }, (_, colIndex) => {
      const slotQrCode = buildSlotQrCode(currentLayoutName, rowIndex, colIndex);
      return { type: 'empty' as const, color: '#e0e0e0', label: '', qrCode: slotQrCode, slotQrCode };
    })];
    setFloorLayout(newLayout);
    saveFloorLocally(newLayout);
    setSelectedSlots(new Set());
  };

  const handleRemoveRow = () => {
    if (floorLayout.length <= 1) return;
    const newLayout = floorLayout.slice(0, floorLayout.length - 1);
    setFloorLayout(newLayout);
    saveFloorLocally(newLayout);
    setSelectedSlots(new Set());
  };

  const handleAddColumn = () => {
    const newColIndex = floorLayout[0]?.length || 0;
    const newLayout = floorLayout.map((row, rowIndex) => {
      const slotQrCode = buildSlotQrCode(currentLayoutName, rowIndex, newColIndex);
      return [...row, { type: 'empty' as const, color: '#e0e0e0', label: '', qrCode: slotQrCode, slotQrCode }];
    });
    setFloorLayout(newLayout);
    saveFloorLocally(newLayout);
    setSelectedSlots(new Set());
  };

  const handleRemoveColumn = () => {
    const currentCols = floorLayout[0]?.length || 0;
    if (currentCols <= 1) return;
    const newLayout = floorLayout.map(row => row.slice(0, currentCols - 1));
    setFloorLayout(newLayout);
    saveFloorLocally(newLayout);
    setSelectedSlots(new Set());
  };

  const handleOpenBulkEdit = () => {
    if (selectedSlots.size === 0) return;
    const [firstKey] = Array.from(selectedSlots);
    const [r, c] = firstKey.split('-').map(Number);
    const slot = floorLayout[r][c];
    setSelectedFloorSlot({ row: r, col: c });
    setSlotEditForm({
      type: slot.type,
      color: slot.type === 'occupied' ? slot.color : '#4CAF50',
      label: slot.label || '',
      batchNumber: slot.batchNumber || '',
      trollyNumber: slot.trollyNumber || '',
    });
    setSlotDialogOpen(true);
  };

  const openSingleSlotEditor = (row: number, col: number) => {
    const slot = floorLayout[row][col];
    setSelectedSlots(new Set([`${row}-${col}`]));
    setSelectedFloorSlot({ row, col });
    setSlotEditForm({
      type: slot.type,
      color: slot.type === 'occupied' ? slot.color : '#4CAF50',
      label: slot.label || '',
      batchNumber: slot.batchNumber || '',
      trollyNumber: slot.trollyNumber || '',
    });
    setSlotDialogOpen(true);
  };

  const handleSlotSave = () => {
    const defaultColor = slotEditForm.type === 'path' ? '#FDD835'
      : slotEditForm.type === 'blocked' ? '#9E9E9E'
      : slotEditForm.type === 'boundary' ? '#7B1FA2'
      : slotEditForm.type === 'empty' ? '#e0e0e0'
      : slotEditForm.color;
    const newLayout = floorLayout.map((r, ri) =>
      r.map((slot, ci) => {
        const key = `${ri}-${ci}`;
        if (selectedSlots.has(key)) {
          const nextQrCode = buildSlotQrCode(currentLayoutName || DEFAULT_LAYOUT_NAME, ri, ci, slotEditForm.label || slotEditForm.trollyNumber);
          return { type: slotEditForm.type, color: defaultColor, label: slotEditForm.label, batchNumber: slotEditForm.batchNumber, trollyNumber: slotEditForm.trollyNumber, qrCode: nextQrCode, slotQrCode: nextQrCode };
        }
        return slot;
      })
    ) as FloorSlot[][];
    setFloorLayout(newLayout);
    saveFloorLocally(newLayout);
    setSlotDialogOpen(false);
    setSelectedSlots(new Set());
    setSelectedFloorSlot(null);
  };

  const handleResetFloor = () => {
    if (!window.confirm('Reset the entire floor layout? This cannot be undone.')) return;
    const fresh = makeEmptyFloor(floorLayout.length || DEFAULT_FLOOR_ROWS, floorLayout[0]?.length || DEFAULT_FLOOR_COLS, currentLayoutName);
    setFloorLayout(fresh);
    saveFloorLocally(fresh);
    setSelectedSlots(new Set());
  };

  const getFloorStats = () => {
    const flat = floorLayout.flat();
    const total = flat.length;
    const occupied = flat.filter(s => s.type === 'occupied').length;
    const path = flat.filter(s => s.type === 'path').length;
    const blocked = flat.filter(s => s.type === 'blocked').length;
    const usable = total - path - blocked;
    return { total, occupied, empty: usable - occupied, path, blocked, usable, occupancyRate: usable > 0 ? Math.round((occupied / usable) * 100) : 0 };
  };
  // ─────────────────────────────────────────────────────────────

  const handleDownloadPDF = async () => {
    try {
      // Show loading indicator
      setSaveLoading(true);

      // Use filteredStocks (already filtered by all active filters + sorted)
      const allStocks = filteredStocks;

      console.log('Using filtered stocks:', allStocks.length);

      if (allStocks.length === 0) {
        alert('No stock data found matching the current filters.');
        setSaveLoading(false);
        return;
      }

      // Create PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Add header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('PFP Trolly Stock Report', pageWidth / 2, 15, { align: 'center' });
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 22, { align: 'center' });
      
      // Add filter summary line
      const filterParts: string[] = [];
      if (statusFilter) filterParts.push(`Status: ${statusFilter}`);
      if (pfpTypeFilter) filterParts.push(`Type: ${pfpTypeFilter}`);
      if (colorFilter) filterParts.push(`Color: ${colorFilter}`);
      if (customerFilter) filterParts.push(`Customer: ${customerFilter}`);
      if (fabricationFilter) filterParts.push(`Fab: ${fabricationFilter}`);
      if (searchTerm) filterParts.push(`Search: "${searchTerm}"`);
      if (stockSort === 'newest') filterParts.push('Sorted: Newest First');
      if (stockSort === 'oldest') filterParts.push('Sorted: Oldest First');
      if (stockSort === 'location') filterParts.push('Sorted: By Location');
      if (filterParts.length > 0) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text(`Filters: ${filterParts.join(' | ')}`, pageWidth / 2, 27, { align: 'center' });
      }

      // Add summary
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Batches: ${allStocks.length}`, 14, 34);
      const totalTrollies = allStocks.reduce((sum: number, s: any) => sum + Number(s.trolly_count || 0), 0);
      doc.text(`Total Trollies: ${totalTrollies}`, pageWidth - 14, 34, { align: 'right' });

      // Prepare all data for the table
      const tableData: any[] = [];
      
      // Fetch trolly items for each batch
      for (let i = 0; i < allStocks.length; i++) {
        const stock = allStocks[i];
        
        // Fetch trolly items for this batch
        let trollyItems: any[] = [];
        try {
          const items = await pfpTrollyStockService.getTrollyItems(stock.batch_number);
          trollyItems = items.trollyItems || [];
          console.log(`Batch ${stock.batch_number}: ${trollyItems.length} trollies`);
        } catch (error) {
          console.error(`Error fetching trolly items for ${stock.batch_number}:`, error);
        }

        // Add trolly items to table, or batch-level data if no trollies exist
        // Only include trollies that still have stock (current_quantity > 0)
        const activeTrollyItems = trollyItems.filter((item: any) => parseFloat(Number(item.current_quantity || 0).toFixed(2)) > 0);
        if (activeTrollyItems.length > 0) {
          // Has active trolley items - show each trolley with details
          const dateIn = stock.date_in ? new Date(stock.date_in).toLocaleDateString('en-GB') : '-';
          activeTrollyItems.forEach((item: any) => {
            const currentQuantityLBS = Number(item.current_quantity || 0);
            const currentQuantityKG = (currentQuantityLBS * 0.453592).toFixed(2);
            
            tableData.push([
              stock.batch_number,
              dateIn,
              item.trolly_number || 'N/A',
              item.location || '-',
              `${currentQuantityKG} KG (${currentQuantityLBS.toFixed(2)} LBS)`
            ]);
          });
        } else if (trollyItems.length > 0) {
          // All trollies depleted — skip this batch
          // (no rows added)
        } else {
          // No trolley items - show batch-level data
          const currentWeightLBS = Number(stock.current_weight || 0);
          const currentWeightKG = (currentWeightLBS * 0.453592).toFixed(2);
          const dateInBatch = stock.date_in ? new Date(stock.date_in).toLocaleDateString('en-GB') : '-';
          
          tableData.push([
            stock.batch_number,
            dateInBatch,
            '-- Batch Level --',
            stock.color_name || '-',
            `${currentWeightKG} KG (${currentWeightLBS.toFixed(2)} LBS)`
          ]);
        }

        // Progress indicator
        if ((i + 1) % 10 === 0 && i + 1 < allStocks.length) {
          console.log(`Processing batch ${i + 1} of ${allStocks.length}...`);
        }
      }

      console.log('Total table rows:', tableData.length);

      // Sort by location if requested (location is column index 3)
      if (stockSort === 'location') {
        tableData.sort((a, b) => {
          const locA = (a[3] || '').toString().trim();
          const locB = (b[3] || '').toString().trim();
          const numA = parseInt(locA, 10);
          const numB = parseInt(locB, 10);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          return locA.localeCompare(locB);
        });
      }

      if (tableData.length === 0) {
        alert('No stock data found to generate PDF.');
        setSaveLoading(false);
        return;
      }

      // Create the table
      autoTable(doc, {
        startY: filterParts.length > 0 ? 42 : 40,
        head: [['Batch Number', 'Date In', 'Trolly Number', 'Location/Color', 'Current Stock']],
        body: tableData,
        theme: 'grid',
        headStyles: { 
          fillColor: [0, 105, 92],
          fontSize: 10,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: { 
          fontSize: 9 
        },
        columnStyles: {
          0: { cellWidth: 40, halign: 'left' },
          1: { cellWidth: 28, halign: 'center' },
          2: { cellWidth: 35, halign: 'left' },
          3: { cellWidth: 40, halign: 'left' },
          4: { cellWidth: 'auto', halign: 'right' }
        },
        margin: { left: 14, right: 14 },
        didDrawPage: (data: any) => {
          // Add footer to each page
          const pageNumber = (doc as any).internal.getCurrentPageInfo().pageNumber;
          doc.setFontSize(8);
          doc.setTextColor(128, 128, 128);
          doc.text(
            `Page ${pageNumber}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' }
          );
        }
      });

      // Save PDF
      const fileName = `PFP_Stock_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      console.log('PDF generated successfully:', fileName);
      setSaveLoading(false);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
      setSaveLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      setSaveLoading(true);

      const allStocks = filteredStocks;

      if (allStocks.length === 0) {
        alert('No stock data found matching the current filters.');
        setSaveLoading(false);
        return;
      }

      // Escape a cell value for CSV
      const escapeCell = (val: string) => {
        if (val.includes(',') || val.includes('"') || val.includes('\n')) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      };

      const rows: string[][] = [];

      // Metadata header rows (mirrors the PDF filter summary)
      rows.push(['PFP Trolly Stock Report']);
      rows.push([`Generated: ${new Date().toLocaleString()}`]);

      const filterParts: string[] = [];
      if (statusFilter) filterParts.push(`Status: ${statusFilter}`);
      if (pfpTypeFilter) filterParts.push(`Type: ${pfpTypeFilter}`);
      if (colorFilter) filterParts.push(`Color: ${colorFilter}`);
      if (customerFilter) filterParts.push(`Customer: ${customerFilter}`);
      if (fabricationFilter) filterParts.push(`Fab: ${fabricationFilter}`);
      if (searchTerm) filterParts.push(`Search: "${searchTerm}"`);
      if (stockSort === 'newest') filterParts.push('Sorted: Newest First');
      if (stockSort === 'oldest') filterParts.push('Sorted: Oldest First');
      if (stockSort === 'location') filterParts.push('Sorted: By Location');
      if (filterParts.length > 0) {
        rows.push([`Filters: ${filterParts.join(' | ')}`]);
      }

      const totalTrollies = allStocks.reduce((sum: number, s: any) => sum + Number(s.trolly_count || 0), 0);
      rows.push([`Total Batches: ${allStocks.length}`, `Total Trollies: ${totalTrollies}`]);
      rows.push([]); // blank separator

      // Data header row
      rows.push(['Batch Number', 'Date In', 'PFP Type', 'Color', 'Customer', 'Trolly Number', 'Location', 'Current Stock (KG)', 'Current Stock (LBS)']);

      for (let i = 0; i < allStocks.length; i++) {
        const stock = allStocks[i];
        let trollyItems: any[] = [];
        try {
          const items = await pfpTrollyStockService.getTrollyItems(stock.batch_number);
          trollyItems = items.trollyItems || [];
        } catch (error) {
          console.error(`Error fetching trolly items for ${stock.batch_number}:`, error);
        }

        const activeTrollyItems = trollyItems.filter((item: any) => parseFloat(Number(item.current_quantity || 0).toFixed(2)) > 0);
        const dateIn = stock.date_in ? new Date(stock.date_in).toLocaleDateString('en-GB') : '-';
        const pfpType = stock.pfp_type || '-';
        const color = stock.color_name || '-';
        const customer = stock.customer || '-';

        if (activeTrollyItems.length > 0) {
          activeTrollyItems.forEach((item: any) => {
            const lbs = Number(item.current_quantity || 0);
            const kg = (lbs * 0.453592).toFixed(2);
            rows.push([stock.batch_number, dateIn, pfpType, color, customer, item.trolly_number || 'N/A', item.location || '-', kg, lbs.toFixed(2)]);
          });
        } else if (trollyItems.length === 0) {
          // No trolly items at all — show batch-level row
          const lbs = Number(stock.current_weight || 0);
          const kg = (lbs * 0.453592).toFixed(2);
          rows.push([stock.batch_number, dateIn, pfpType, color, customer, '-- Batch Level --', '-', kg, lbs.toFixed(2)]);
        }
        // else: all trollies depleted — skip (mirrors PDF behaviour)
      }

      // Sort data rows by location if requested (location is column index 6, after the header/meta rows)
      if (stockSort === 'location') {
        const headerRows: string[][] = [];
        const dataRows: string[][] = [];
        let pastHeader = false;
        for (const row of rows) {
          if (!pastHeader && row[0] === 'Batch Number') { pastHeader = true; headerRows.push(row); continue; }
          if (!pastHeader) { headerRows.push(row); continue; }
          dataRows.push(row);
        }
        dataRows.sort((a, b) => {
          const locA = (a[6] || '').trim();
          const locB = (b[6] || '').trim();
          const numA = parseInt(locA, 10);
          const numB = parseInt(locB, 10);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          return locA.localeCompare(locB);
        });
        rows.length = 0;
        rows.push(...headerRows, ...dataRows);
      }

      const csvContent = rows.map(row => row.map(escapeCell).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PFP_Stock_Report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSaveLoading(false);
    } catch (error) {
      console.error('Error generating CSV:', error);
      alert('Failed to generate CSV. Please try again.');
      setSaveLoading(false);
    }
  };

  const handleReleaseSubmit = async () => {
    try {
      setSaveLoading(true);
      setSaveError(null);

      if (trollyReleases.length === 0) {
        setSaveError('Please select at least one trolly to release');
        setSaveLoading(false);
        return;
      }

      const data = {
        ...releaseForm,
        trolly_releases: trollyReleases,
        is_return: isReturnMode,
        return_department: isReturnMode ? returnDepartment : null,
        return_reason: isReturnMode ? returnReason : null,
      };

      if (selectedRelease) {
        await pfpTrollyReleaseService.update(selectedRelease.id, data);
      } else {
        await pfpTrollyReleaseService.create(data);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      handleReleaseDialogClose();
      fetchData();
      fetchStatistics();
      fetchActiveBatches();
    } catch (error: any) {
      console.error('Error saving release:', error);
      setSaveError(error.response?.data?.error || 'Failed to save release');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteStock = async (stock: PfpTrollyStock) => {
    if (!window.confirm(`Are you sure you want to delete batch ${stock.batch_number}?`)) {
      return;
    }

    try {
      await pfpTrollyStockService.delete(stock.id);
      fetchData();
      fetchStatistics();
      fetchActiveBatches();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete stock');
    }
  };

  const handleDeleteRelease = async (release: PfpTrollyRelease) => {
    if (!window.confirm(`Are you sure you want to delete release ${release.reference_number}?`)) {
      return;
    }

    try {
      await pfpTrollyReleaseService.delete(release.id);
      fetchData();
      fetchStatistics();
      fetchActiveBatches();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete release');
    }
  };

  const handleViewDetails = async (stock: PfpTrollyStock) => {
    try {
      setViewStock(stock);
      setStockReleases([]);
      setViewTrollyItems([]);

      const [releaseResponse, trollyResponse] = await Promise.all([
        pfpTrollyReleaseService.getByStockId(stock.id),
        pfpTrollyStockService.getTrollyItems(stock.batch_number.trim())
      ]);

      let batchReleases = Array.isArray(releaseResponse?.releases) ? releaseResponse.releases : [];
      if (batchReleases.length === 0) {
        const fallbackResponse = await pfpTrollyReleaseService.getAll({ batch_number: stock.batch_number.trim() });
        batchReleases = Array.isArray(fallbackResponse?.releases) ? fallbackResponse.releases : [];
      }

      let batchTrolleys = Array.isArray(trollyResponse)
        ? trollyResponse
        : Array.isArray(trollyResponse?.trollyItems)
          ? trollyResponse.trollyItems
          : Array.isArray(trollyResponse?.items)
            ? trollyResponse.items
            : [];

      if (batchTrolleys.length === 0 && stock.trolly_number) {
        batchTrolleys = [{
          id: `legacy-${stock.id}`,
          trolly_number: stock.trolly_number,
          location: stock.location || null,
          initial_quantity: stock.initial_weight,
          current_quantity: stock.current_weight,
          legacy_record: true
        }];
      }

      setStockReleases(batchReleases);
      setViewTrollyItems(batchTrolleys);
      setDetailsDialogOpen(true);
    } catch (error: any) {
      console.error('Error fetching stock releases:', error);
      alert(error.response?.data?.error || 'Unable to load trolley and release details for this batch.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Depleted':
        return 'error';
      case 'On Hold':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPfpTypeColor = (type: string) => {
    if (type === 'Direct') return 'success';
    if (type === 'Digi') return 'info';
    return 'warning';
  };

  const filteredStocks = (() => {
    let filtered = stocks.filter((stock) => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      return (
        stock.batch_number.toLowerCase().includes(s) ||
        stock.customer?.toLowerCase().includes(s) ||
        stock.color_name?.toLowerCase().includes(s) ||
        (stock as any).trolly_numbers?.toLowerCase().includes(s)
      );
    });
    // Batch-level LBS range filter
    if (lbsFilterTarget === 'batch' && (lbsMin || lbsMax)) {
      filtered = filtered.filter(s => {
        const w = Number(s.current_weight || 0);
        if (lbsMin && w < Number(lbsMin)) return false;
        if (lbsMax && w > Number(lbsMax)) return false;
        return true;
      });
    }
    if (stockSort === 'newest') return [...filtered].sort((a, b) => new Date(b.date_in).getTime() - new Date(a.date_in).getTime());
    if (stockSort === 'oldest') return [...filtered].sort((a, b) => new Date(a.date_in).getTime() - new Date(b.date_in).getTime());
    if (stockSort === 'location') return [...filtered].sort((a, b) => (a as any).first_location || '' < (b as any).first_location || '' ? -1 : 1);
    return filtered;
  })();

  const showTrolleyView = (lbsFilterTarget === 'trolley' && (!!lbsMin || !!lbsMax)) || stockSort === 'location';

  const filteredFlatTrollies = (() => {
    let items = flatTrollyItems.filter(item => {
      const w = item.current_quantity;
      if (lbsMin && w < Number(lbsMin)) return false;
      if (lbsMax && w > Number(lbsMax)) return false;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        if (!item.batch_number.toLowerCase().includes(s) &&
            !item.customer.toLowerCase().includes(s) &&
            !item.trolly_number.toLowerCase().includes(s) &&
            !item.color_name.toLowerCase().includes(s)) return false;
      }
      if (pfpTypeFilter && item.pfp_type !== pfpTypeFilter) return false;
      if (colorFilter && item.color_name !== colorFilter) return false;
      if (customerFilter && item.customer !== customerFilter) return false;
      return true;
    });
    if (stockSort === 'newest') return [...items].sort((a, b) => new Date(b.date_in).getTime() - new Date(a.date_in).getTime());
    if (stockSort === 'oldest') return [...items].sort((a, b) => new Date(a.date_in).getTime() - new Date(b.date_in).getTime());
    if (stockSort === 'location') return [...items].sort((a, b) => {
      const locA = (a.location || '').trim();
      const locB = (b.location || '').trim();
      const numA = parseInt(locA, 10);
      const numB = parseInt(locB, 10);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return locA.localeCompare(locB);
    });
    return items;
  })();

  // Load flat trolley items lazily when trolley LBS filter mode is selected OR location sort is active
  useEffect(() => {
    if ((lbsFilterTarget === 'trolley' || stockSort === 'location') && flatTrollyItems.length === 0 && stocks.length > 0) {
      const load = async () => {
        setFlatTrollyLoading(true);
        const items: Array<{ batch_number: string; date_in: string; color_name: string; customer: string; pfp_type: string; trolly_number: string; location: string; current_quantity: number }> = [];
        for (const stock of stocks) {
          try {
            const res = await pfpTrollyStockService.getTrollyItems(stock.batch_number);
            const trollies: any[] = res.trollyItems || [];
            trollies.forEach((item: any) => {
              const qty = parseFloat(Number(item.current_quantity || 0).toFixed(2));
              if (qty > 0) {
                items.push({
                  batch_number: stock.batch_number,
                  date_in: stock.date_in,
                  color_name: stock.color_name || '',
                  customer: stock.customer || '',
                  pfp_type: stock.pfp_type,
                  trolly_number: item.trolly_number,
                  location: item.location || '-',
                  current_quantity: qty,
                });
              }
            });
          } catch {}
        }
        setFlatTrollyItems(items);
        setFlatTrollyLoading(false);
      };
      load();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lbsFilterTarget, stockSort, stocks]);

  const filteredReleases = releases.filter((release) => {
    const s = searchTerm.toLowerCase();
    return (
      (release.reference_number || '').toLowerCase().includes(s) ||
      (release.batch_number || '').toLowerCase().includes(s) ||
      (release.p_batch || '').toLowerCase().includes(s) ||
      (release.customer || '').toLowerCase().includes(s)
    );
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#00695C' }}>
          PFP Trolly Management System
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={tabValue === 0 ? 'Download PDF for current filters & sort' : 'Download Stock Report (PDF)'}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleDownloadPDF}
              startIcon={saveLoading ? <CircularProgress size={16} /> : <PdfIcon />}
              size={isMobile ? 'small' : 'medium'}
              disabled={saveLoading}
            >
              {!isMobile && (saveLoading ? 'Generating...' : tabValue === 0 ? 'PDF (Filtered)' : 'PDF Report')}
            </Button>
          </Tooltip>
          <Tooltip title={tabValue === 0 ? 'Download CSV for current filters & sort' : 'Download Stock Report (CSV)'}>
            <Button
              variant="outlined"
              color="success"
              onClick={handleDownloadCSV}
              startIcon={saveLoading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              size={isMobile ? 'small' : 'medium'}
              disabled={saveLoading}
            >
              {!isMobile && (saveLoading ? 'Generating...' : tabValue === 0 ? 'CSV (Filtered)' : 'CSV Report')}
            </Button>
          </Tooltip>
          <IconButton onClick={fetchData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(false)}>
          Operation completed successfully
        </Alert>
      )}

      {stocksError && tabValue === 0 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStocksError(null)}>
          Could not load trolley stock: {stocksError}
        </Alert>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Batches
                </Typography>
                <Typography variant="h4">{filteredStocks.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Available Trollies
                </Typography>
                <Typography variant="h4" color="success.main">
                  {filteredStocks.filter(s => s.status === 'Active').reduce((sum, stock) => sum + (stock.trolly_count || 0), 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Current Stock (LBS)
                </Typography>
                <Typography variant="h4">
                  {filteredStocks.reduce((sum, stock) => sum + Number(stock.current_weight || 0), 0).toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<InventoryIcon />} label={isMobile ? "Stock" : "Stock Management"} />
          <Tab icon={<TrollyIcon />} label={isMobile ? "Release" : "Release Management"} />
          <Tab icon={<FloorLayoutIcon />} label={isMobile ? "Floor" : "Floor Layout"} />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {tabValue !== 2 && (
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <TextField
              placeholder={tabValue === 1 ? 'Search by Batch Number or P Batch...' : 'Search...'}
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />

            {tabValue === 0 && (
              <>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Depleted">Depleted</MenuItem>
                  <MenuItem value="On Hold">On Hold</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  label="PFP Type"
                  value={pfpTypeFilter}
                  onChange={(e) => setPfpTypeFilter(e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Direct">Direct</MenuItem>
                  <MenuItem value="Stock">Stock</MenuItem>
                  <MenuItem value="Digi">Digi</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Color"
                  value={colorFilter}
                  onChange={(e) => setColorFilter(e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {Array.from(new Set(stocks.map(s => s.color_name).filter(Boolean))).sort().map(color => (
                    <MenuItem key={color} value={color}>{color}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Customer"
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {Array.from(new Set(stocks.map(s => s.customer).filter(Boolean))).sort().map(customer => (
                    <MenuItem key={customer} value={customer}>{customer}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Fabrication"
                  value={fabricationFilter}
                  onChange={(e) => setFabricationFilter(e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {Array.from(new Set(stocks.map(s => s.fabrication).filter(Boolean))).sort().map(fabrication => (
                    <MenuItem key={fabrication} value={fabrication}>{fabrication}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Sort By"
                  value={stockSort}
                  onChange={(e) => setStockSort(e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">Default</MenuItem>
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                  <MenuItem value="location">By Location</MenuItem>
                </TextField>

                {/* LBS Range Filter */}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <TextField
                  select
                  size="small"
                  label="LBS Filter By"
                  value={lbsFilterTarget}
                  onChange={(e) => { setLbsFilterTarget(e.target.value as 'batch' | 'trolley'); setPage(0); }}
                  sx={{ minWidth: 130 }}
                >
                  <MenuItem value="batch">Batch LBS</MenuItem>
                  <MenuItem value="trolley">Trolley LBS</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  label="Min LBS"
                  type="number"
                  value={lbsMin}
                  onChange={(e) => { setLbsMin(e.target.value); setPage(0); }}
                  sx={{ width: 110 }}
                  inputProps={{ min: 0, step: 1 }}
                />
                <TextField
                  size="small"
                  label="Max LBS"
                  type="number"
                  value={lbsMax}
                  onChange={(e) => { setLbsMax(e.target.value); setPage(0); }}
                  sx={{ width: 110 }}
                  inputProps={{ min: 0, step: 1 }}
                />
                {(lbsMin || lbsMax) && (
                  <Button size="small" variant="outlined" color="error"
                    onClick={() => { setLbsMin(''); setLbsMax(''); setPage(0); }}>
                    Clear LBS
                  </Button>
                )}
              </>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {!isMobile && tabValue === 0 && canEdit && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleStockDialogOpen()}
                sx={{ display: { xs: 'none', md: 'flex' } }}
              >
                Add Stock
              </Button>
            )}

            {!isMobile && tabValue === 1 && canRelease && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleReleaseDialogOpen()}
                sx={{ display: { xs: 'none', md: 'flex' } }}
              >
                Add Release
              </Button>
            )}
          </Box>
          )} {/* end tabValue !== 2 filter bar */}

          {loading && tabValue !== 2 ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : tabValue === 0 ? (
            <>
              {showTrolleyView ? (
                /* ── Trolley LBS Range View ── */
                flatTrollyLoading ? (
                  <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {stockSort === 'location'
                        ? <>Showing <strong>{filteredFlatTrollies.length}</strong> trolley{filteredFlatTrollies.length !== 1 ? 's' : ''} sorted by location</>
                        : <>Showing <strong>{filteredFlatTrollies.length}</strong> trolley{filteredFlatTrollies.length !== 1 ? 's' : ''} with LBS{lbsMin ? ` ≥ ${lbsMin}` : ''}{lbsMax ? ` ≤ ${lbsMax}` : ''}</>
                      }
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Batch Number</TableCell>
                            <TableCell>Date In</TableCell>
                            <TableCell>Trolly Number</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Color</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell align="right">Current Stock (LBS)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredFlatTrollies
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((item, idx) => (
                              <TableRow key={`${item.batch_number}-${item.trolly_number}-${idx}`} hover>
                                <TableCell><Typography variant="body2" fontWeight="bold">{item.batch_number}</Typography></TableCell>
                                <TableCell>{new Date(item.date_in).toLocaleDateString('en-GB')}</TableCell>
                                <TableCell>{item.trolly_number}</TableCell>
                                <TableCell>{item.location}</TableCell>
                                <TableCell>{item.color_name || '-'}</TableCell>
                                <TableCell>{item.customer || '-'}</TableCell>
                                <TableCell align="right">
                                  <Typography fontWeight="bold" color={item.current_quantity < 200 ? 'warning.main' : 'textPrimary'}>
                                    {item.current_quantity.toFixed(2)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          {filteredFlatTrollies.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                <Typography color="text.secondary" sx={{ py: 2 }}>No trolleys found in this LBS range</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={filteredFlatTrollies.length}
                      page={page}
                      onPageChange={(e, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    />
                  </>
                )
              ) : (
              <>
              {/* Desktop Table View */}
              {!isMobile ? (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Batch Number</TableCell>
                          <TableCell>Date In</TableCell>
                          <TableCell>Initial Weight (LBS)</TableCell>
                          <TableCell>Current Weight (LBS)</TableCell>
                          <TableCell>PFP Type</TableCell>
                          <TableCell>No. of Trollies</TableCell>
                          <TableCell>Color</TableCell>
                          <TableCell>Customer</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Released</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredStocks
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((stock) => (
                            <TableRow
                              key={stock.id}
                              hover
                              sx={{ cursor: 'pointer' }}
                              onClick={() => handleViewDetails(stock)}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {stock.batch_number}
                                </Typography>
                              </TableCell>
                              <TableCell>{new Date(stock.date_in).toLocaleDateString()}</TableCell>
                              <TableCell>{Number(stock.initial_weight).toFixed(2)}</TableCell>
                              <TableCell>
                                <Typography
                                  color={
                                    Number(stock.current_weight) < Number(stock.initial_weight) * 0.2
                                      ? 'error'
                                      : 'textPrimary'
                                  }
                                  fontWeight="bold"
                                >
                                  {Number(stock.current_weight).toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={stock.pfp_type}
                                  color={getPfpTypeColor(stock.pfp_type)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {stock.trolly_count || 0}
                                </Typography>
                              </TableCell>
                              <TableCell>{stock.color_name || '-'}</TableCell>
                              <TableCell>{stock.customer || '-'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={stock.status}
                                  color={getStatusColor(stock.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {Number(stock.total_released || 0).toFixed(2)} LBS
                                  <br />
                                  <Typography variant="caption" color="textSecondary">
                                    ({stock.release_count} releases)
                                  </Typography>
                                </Typography>
                              </TableCell>
                              <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                <Tooltip title="View Details">
                                  <IconButton size="small" onClick={() => handleViewDetails(stock)} color="primary">
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Manage Trollies">
                                  <IconButton size="small" onClick={() => handleOpenManageTrollies(stock)} sx={{ color: '#00695C' }}>
                                    <TuneIcon />
                                  </IconButton>
                                </Tooltip>
                                {canEdit && (
                                  <>
                                    <Tooltip title="Edit Batch">
                                      <IconButton size="small" onClick={() => handleStockDialogOpen(stock)}>
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    {user?.role === 'admin' && (
                                      <Tooltip title="Delete">
                                        <IconButton size="small" onClick={() => handleDeleteStock(stock)} color="error">
                                          <DeleteIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filteredStocks.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                  />
                </>
              ) : (
                /* Mobile Card View */
                <Grid container spacing={2}>
                  {filteredStocks
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((stock) => (
                      <Grid item xs={12} key={stock.id}>
                        <Card
                          sx={{
                            borderLeft: `6px solid ${getStatusColor(stock.status) === 'success' ? '#4CAF50' : getStatusColor(stock.status) === 'warning' ? '#FF9800' : '#9E9E9E'}`,
                            '&:hover': { boxShadow: 6, cursor: 'pointer' },
                            transition: 'box-shadow 0.2s',
                          }}
                          onClick={() => handleViewDetails(stock)}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box>
                                <Typography variant="h6" fontWeight="bold" color="primary">
                                  {stock.batch_number}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Date In: {new Date(stock.date_in).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Chip
                                label={stock.status}
                                color={getStatusColor(stock.status)}
                                size="small"
                              />
                            </Box>
                            
                            <Divider sx={{ my: 1.5 }} />
                            
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Initial Weight</Typography>
                                <Typography variant="body1" fontWeight="bold">{Number(stock.initial_weight).toFixed(2)} LBS</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Current Weight</Typography>
                                <Typography 
                                  variant="body1" 
                                  fontWeight="bold"
                                  color={Number(stock.current_weight) < Number(stock.initial_weight) * 0.2 ? 'error' : 'textPrimary'}
                                >
                                  {Number(stock.current_weight).toFixed(2)} LBS
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">PFP Type</Typography>
                                <Typography variant="body2">
                                  <Chip label={stock.pfp_type} color={getPfpTypeColor(stock.pfp_type)} size="small" />
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Released</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {Number(stock.total_released || 0).toFixed(2)} LBS ({stock.release_count})
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">No. of Trollies</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {stock.trolly_count || 0}
                                </Typography>
                              </Grid>
                              {stock.color_name && (
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="textSecondary">Color</Typography>
                                  <Typography variant="body2">{stock.color_name}</Typography>
                                </Grid>
                              )}
                              {stock.customer && (
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="textSecondary">Customer</Typography>
                                  <Typography variant="body2">{stock.customer}</Typography>
                                </Grid>
                              )}
                            </Grid>
                            
                            <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                              <IconButton size="small" onClick={() => handleViewDetails(stock)} color="primary">
                                <VisibilityIcon />
                              </IconButton>
                              <Tooltip title="Manage Trollies">
                                <IconButton size="small" onClick={() => handleOpenManageTrollies(stock)} sx={{ color: '#00695C' }}>
                                  <TuneIcon />
                                </IconButton>
                              </Tooltip>
                              {canEdit && (
                                <>
                                  <IconButton size="small" onClick={() => handleStockDialogOpen(stock)}>
                                    <EditIcon />
                                  </IconButton>
                                  {user?.role === 'admin' && (
                                    <IconButton size="small" onClick={() => handleDeleteStock(stock)} color="error">
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  {filteredStocks.length === 0 && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="text.secondary">No stock batches found</Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              )}
              {isMobile && filteredStocks.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <TablePagination
                    component="div"
                    count={filteredStocks.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                  />
                </Box>
              )}
            </> /* end normal batch view */
            )} {/* end showTrolleyView ternary */}
            </>
          ) : tabValue === 1 ? (
            <>
              {/* Desktop Table View - Releases */}
              {!isMobile ? (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date & Time</TableCell>
                          <TableCell>Batch Number</TableCell>
                          <TableCell>P Batch</TableCell>
                          <TableCell>Quantity (LBS)</TableCell>
                          <TableCell>Color</TableCell>
                          <TableCell>Customer</TableCell>
                          <TableCell>Released By</TableCell>
                          <TableCell>Remarks</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredReleases
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((release) => (
                            <TableRow key={release.id} hover>
                              <TableCell>
                                {new Date(release.release_datetime).toLocaleString()}
                              </TableCell>
                              <TableCell>{release.batch_number}</TableCell>
                              <TableCell>{release.p_batch || '-'}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                  {Number(release.releasing_quantity).toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell>{release.color_name || '-'}</TableCell>
                              <TableCell>{release.customer || '-'}</TableCell>
                              <TableCell>{release.released_by_name || '-'}</TableCell>
                              <TableCell>{release.remarks || '-'}</TableCell>
                              <TableCell align="center">
                                <Tooltip title="Manage Trollies">
                                  <IconButton size="small" onClick={() => handleOpenManageTrolliesByBatch(release.batch_number)} sx={{ color: '#00695C' }}>
                                    <TuneIcon />
                                  </IconButton>
                                </Tooltip>
                                {canEdit && (
                                  <>
                                    <Tooltip title="Edit">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleReleaseDialogOpen(release)}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    {user?.role === 'admin' && (
                                      <Tooltip title="Delete">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDeleteRelease(release)}
                                          color="error"
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filteredReleases.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                  />
                </>
              ) : (
                <Grid container spacing={2}>
                  {filteredReleases
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((release) => (
                      <Grid item xs={12} key={release.id}>
                        <Card sx={{borderLeft: '6px solid #2196F3','&:hover': { boxShadow: 6 }}}><CardContent><Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                              <Box><Typography variant="h6" fontWeight="bold" color="primary">{release.batch_number}</Typography>
                                <Typography variant="caption" color="textSecondary">{new Date(release.release_datetime).toLocaleString()}</Typography></Box>
                              <Typography variant="h6" fontWeight="bold" color="primary">{Number(release.releasing_quantity).toFixed(2)} LBS</Typography></Box>
                            <Divider sx={{ my: 1.5 }} />
                            <Grid container spacing={1}><Grid item xs={6}><Typography variant="caption" color="textSecondary">P Batch</Typography>
                                <Typography variant="body2" fontWeight="bold">{release.p_batch || '-'}</Typography></Grid>
                              {release.color_name && (<Grid item xs={6}><Typography variant="caption" color="textSecondary">Color</Typography>
                                  <Typography variant="body2">{release.color_name}</Typography></Grid>)}
                              {release.customer && (<Grid item xs={6}><Typography variant="caption" color="textSecondary">Customer</Typography>
                                  <Typography variant="body2">{release.customer}</Typography></Grid>)}
                              <Grid item xs={6}><Typography variant="caption" color="textSecondary">Released By</Typography>
                                <Typography variant="body2">{release.released_by_name || '-'}</Typography></Grid>
                              {release.remarks && (<Grid item xs={12}><Typography variant="caption" color="textSecondary">Remarks</Typography>
                                  <Typography variant="body2">{release.remarks}</Typography></Grid>)}</Grid>
                            {canEdit && (<Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Tooltip title="Manage Trollies"><IconButton size="small" onClick={() => handleOpenManageTrolliesByBatch(release.batch_number)} sx={{ color: '#00695C' }}><TuneIcon /></IconButton></Tooltip>
                                <IconButton size="small" onClick={() => handleReleaseDialogOpen(release)}><EditIcon /></IconButton>
                                {user?.role === 'admin' && (<IconButton size="small" onClick={() => handleDeleteRelease(release)} color="error"><DeleteIcon /></IconButton>)}</Box>)}
                            {!canEdit && (<Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Tooltip title="Manage Trollies"><IconButton size="small" onClick={() => handleOpenManageTrolliesByBatch(release.batch_number)} sx={{ color: '#00695C' }}><TuneIcon /></IconButton></Tooltip>
                              </Box>)}</CardContent></Card></Grid>))}
                  {filteredReleases.length === 0 && (<Grid item xs={12}><Paper sx={{ p: 3, textAlign: 'center' }}><Typography color="text.secondary">No releases found</Typography></Paper></Grid>)}</Grid>)}
              {isMobile && filteredReleases.length > 0 && (<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><TablePagination component="div" count={filteredReleases.length} page={page} onPageChange={(e, newPage) => setPage(newPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => {setRowsPerPage(parseInt(e.target.value, 10));setPage(0);}}/></Box>)}
            </>
          ) : (
            /* ══════════ Floor Layout Tab ══════════ */
            <Box>
              {/* Header */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <Autocomplete
                      freeSolo
                      options={layoutList.map((layout) => layout.name)}
                      value={layoutNameDraft}
                      onInputChange={(_, value) => setLayoutNameDraft(value)}
                      onChange={(_, value) => {
                        if (typeof value === 'string') {
                          setLayoutNameDraft(value);
                          handleLayoutSelect(value);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Location / Layout"
                          size="small"
                          placeholder="Select or type a location name"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
                      <Button
                        variant={floorEditMode ? 'contained' : 'outlined'}
                        color={floorEditMode ? 'warning' : 'primary'}
                        startIcon={floorEditMode ? <LockOpenIcon /> : <EditIcon />}
                        onClick={() => setFloorEditMode(v => !v)}
                        size="small"
                      >
                        {floorEditMode ? 'Editing' : 'Edit Layout'}
                      </Button>
                      {floorEditMode && (
                        <>
                          <Button variant="contained" size="small" onClick={handleSaveLayout} disabled={layoutSaving}>
                            Save Layout
                          </Button>
                          <Button variant="outlined" size="small" onClick={loadFloorLayouts} disabled={layoutLoading}>
                            Reload
                          </Button>
                          <Button variant="outlined" size="small" color="error" onClick={handleDeleteLayout} disabled={!currentLayoutId || layoutSaving}>
                            Delete Layout
                          </Button>
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {floorEditMode && (
                <Paper sx={{ p: 1.5, mb: 2 }}>
                  <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                    <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddRow}>Add Row</Button>
                    <Button variant="outlined" size="small" startIcon={<RemoveIcon />} onClick={handleRemoveRow} disabled={floorLayout.length <= 1}>Remove Row</Button>
                    <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddColumn}>Add Column</Button>
                    <Button variant="outlined" size="small" startIcon={<RemoveIcon />} onClick={handleRemoveColumn} disabled={(floorLayout[0]?.length || 0) <= 1}>Remove Column</Button>
                    <Button variant="outlined" color="error" size="small" onClick={handleResetFloor}>Reset Grid</Button>
                  </Box>
                </Paper>
              )}

              <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
                {[
                  { color: '#e8f5e9', border: '#4CAF50', label: 'Empty' },
                  { color: '#FDD835', border: '#F9A825', label: 'Path' },
                  { color: '#9E9E9E', border: '#616161', label: 'Blocked' },
                  { color: '#7B1FA2', border: '#4A148C', label: 'Boundary' },
                  { color: '#2196F3', border: '#1565C0', label: 'Slot' },
                ].map(l => (
                  <Box key={l.label} display="flex" alignItems="center" gap={0.5}>
                    <Box sx={{ width: 18, height: 18, bgcolor: l.color, border: `2px solid ${l.border}`, borderRadius: '3px', flexShrink: 0 }} />
                    <Typography variant="caption">{l.label}</Typography>
                  </Box>
                ))}
              </Box>

              {floorEditMode && selectedSlots.size > 0 && (
                <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#E3F2FD', border: '1px solid #90CAF9' }}>
                  <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#1565C0' }}>
                      {selectedSlots.size} selected
                    </Typography>
                    <Button size="small" variant="contained" color="success" onClick={() => applyDemarcationToSelected('occupied')}>Place Slot</Button>
                    <Button size="small" variant="contained" onClick={handleOpenBulkEdit} sx={{ bgcolor: '#1565C0', '&:hover': { bgcolor: '#0D47A1' } }}>
                      Slot Details
                    </Button>
                    <Button size="small" variant="contained" sx={{ bgcolor: '#F9A825', '&:hover': { bgcolor: '#F57F17' } }} onClick={() => applyDemarcationToSelected('path')}>Mark Path</Button>
                    <Button size="small" variant="contained" color="inherit" onClick={() => applyDemarcationToSelected('blocked')}>Mark Blocked</Button>
                    <Button size="small" variant="contained" sx={{ bgcolor: '#7B1FA2', '&:hover': { bgcolor: '#4A148C' } }} onClick={() => applyDemarcationToSelected('boundary')}>Mark Boundary</Button>
                    <Button size="small" variant="contained" color="secondary" onClick={() => {
                      openMergeDialog();
                    }}>Merge QR</Button>
                    <Button size="small" variant="outlined" onClick={() => applyDemarcationToSelected('empty')}>Clear</Button>
                    {SLOT_COLORS.map((color) => (
                      <Box key={color} onClick={() => applyDemarcationToSelected('occupied', color)} sx={{ width: 18, height: 18, bgcolor: color, border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,0.3)', borderRadius: '4px', cursor: 'pointer' }} />
                    ))}
                    <Button size="small" variant="outlined" onClick={() => setSelectedSlots(new Set())}>Clear Selection</Button>
                  </Box>
                </Paper>
              )}

              {/* Grid */}
              <Box
                onMouseUp={() => setIsDragSelecting(false)}
                onMouseLeave={() => setIsDragSelecting(false)}
                sx={{
                  overflowX: 'auto',
                  pb: 2,
                  p: 1,
                  borderRadius: 1,
                  border: '1px solid #CFD8DC',
                  backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.06) 1px, transparent 1px)',
                  backgroundSize: '68px 58px',
                  backgroundColor: '#FAFAFA',
                }}
              >
                {/* Rows */}
                {floorLayout.map((row, rowIdx) => (
                  <Box key={rowIdx} display="flex" mb={0.5}>
                    {/* Slots */}
                    {row.map((slot, colIdx) => {
                      const slotId = `R${rowIdx + 1}C${colIdx + 1}`;
                      const slotLocationKey = (slot.trollyNumber || slot.label || '').trim().toUpperCase();
                      const slotQrCode = buildSlotQrCode(currentLayoutName, rowIdx, colIdx, slot.label || slot.trollyNumber);
                      const qrMatchedTrolleys = allTrollyItems.filter(
                        t => t.location_qr_code === slotQrCode && Number(t.current_quantity || 0) > 0
                      );
                      const legacyMatchedTrolleys = slotLocationKey
                        ? allTrollyItems.filter(
                            t =>
                              !t.location_qr_code &&
                              (t.location || '').trim().toUpperCase() === slotLocationKey &&
                              Number(t.current_quantity || 0) > 0
                          )
                        : [];
                      const matchedTrolleys = qrMatchedTrolleys.length > 0 ? qrMatchedTrolleys : legacyMatchedTrolleys;
                      const latestMatchedTrolley = matchedTrolleys.length > 0
                        ? matchedTrolleys.reduce((latest, current) => {
                            const latestTime = new Date(latest.updated_at || 0).getTime();
                            const currentTime = new Date(current.updated_at || 0).getTime();
                            return currentTime >= latestTime ? current : latest;
                          })
                        : null;
                      const displayTrolleys = latestMatchedTrolley ? [latestMatchedTrolley] : [];
                      const bgColor = slot.type === 'empty' ? '#e8f5e9'
                        : slot.type === 'path' ? '#FDD835'
                        : slot.type === 'blocked' ? '#BDBDBD'
                        : slot.type === 'boundary' ? '#CE93D8'
                        : slot.color;
                      const borderColor = slot.type === 'path' ? '#F9A825'
                        : slot.type === 'blocked' ? '#757575'
                        : slot.type === 'boundary' ? '#7B1FA2'
                        : slot.type === 'occupied' ? slot.color
                        : '#A5D6A7';
                      const textColor = slot.type === 'occupied' ? 'white' : 'text.secondary';
                      const isSelected = selectedSlots.has(`${rowIdx}-${colIdx}`);
                      const showTrolleyHover = slot.type === 'occupied';
                          const tooltipContent = displayTrolleys.length > 0 ? (
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="caption" fontWeight={700} display="block" sx={{ color: '#fff' }}>
                            Location {slotLocationKey}
                          </Typography>
                          <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.3)' }} />
                              {displayTrolleys.map((trolley, idx) => (
                            <Box key={`${slotId}-${idx}`} sx={{ mb: 0.5 }}>
                              <Typography variant="caption" display="block">Trolley Number: <strong>{trolley.trolly_number}</strong></Typography>
                              <Typography variant="caption" display="block">Initial Quantity: <strong>{trolley.initial_quantity.toFixed(2)} LBS</strong></Typography>
                              <Typography variant="caption" display="block">Current Quantity: <strong>{trolley.current_quantity.toFixed(2)} LBS</strong></Typography>
                              <Typography variant="caption" display="block">Batch Number: <strong>{trolley.batch_number}</strong></Typography>
                              <Typography variant="caption" display="block">PFP Type: <strong>{trolley.pfp_type || '-'}</strong></Typography>
                              <Typography variant="caption" display="block">Date In: <strong>{trolley.date_in ? new Date(trolley.date_in).toLocaleDateString('en-GB') : '-'}</strong></Typography>
                                  {idx < displayTrolleys.length - 1 && <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.2)' }} />}
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="caption" fontWeight={700} display="block" sx={{ color: '#fff' }}>Location {slotLocationKey || slotId}</Typography>
                          <Typography variant="caption" display="block">No trolley details found for this location.</Typography>
                        </Box>
                      );
                      return (
                        <Tooltip key={colIdx} title={showTrolleyHover ? tooltipContent : ''} disableHoverListener={!showTrolleyHover} arrow placement="top" enterDelay={200} componentsProps={{ tooltip: { sx: { bgcolor: '#212121', maxWidth: 220 } } }}>
                          <Box
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleSlotMouseDown(rowIdx, colIdx);
                            }}
                            onMouseEnter={() => handleSlotMouseEnter(rowIdx, colIdx)}
                            onClick={() => {
                              if (!floorEditMode) return;
                              if (!isDragSelecting) handleSlotClick(rowIdx, colIdx);
                            }}
                            onDoubleClick={() => {
                              if (!floorEditMode) return;
                              openSingleSlotEditor(rowIdx, colIdx);
                            }}
                            sx={{
                              width: 68, minWidth: 68, height: 58,
                              mr: 0.5,
                              bgcolor: bgColor,
                              border: isSelected ? '3px solid #1565C0' : `2px solid ${borderColor}`,
                              borderRadius: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: floorEditMode ? 'crosshair' : 'default',
                              transition: 'all 0.12s',
                              boxShadow: isSelected ? '0 0 0 3px #90CAF9' : 'none',
                              '&:hover': floorEditMode ? { boxShadow: isSelected ? '0 0 0 3px #90CAF9, 0 4px 8px rgba(0,0,0,0.2)' : '0 4px 8px rgba(0,0,0,0.2)', transform: 'scale(1.06)', zIndex: 1 } : {},
                              position: 'relative',
                              overflow: 'hidden',
                              userSelect: 'none',
                            }}
                          >
                            {/* Beeping dot indicator for occupied slots with no trolleys */}
                            {slot.type === 'occupied' && matchedTrolleys.length === 0 && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  left: 4,
                                  width: 8,
                                  height: 8,
                                  bgcolor: '#FFC107',
                                  borderRadius: '50%',
                                  animation: 'beep 1.5s ease-in-out infinite',
                                  boxShadow: '0 0 4px rgba(255, 193, 7, 0.6)',
                                  '@keyframes beep': {
                                    '0%, 100%': {
                                      opacity: 1,
                                      transform: 'scale(1)',
                                    },
                                    '50%': {
                                      opacity: 0.3,
                                      transform: 'scale(0.8)',
                                    },
                                  },
                                }}
                              />
                            )}
                            
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: textColor, letterSpacing: '0.2px', maxWidth: '96%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {slot.type === 'occupied'
                                    ? `${slotLocationKey || slotId}`
                                    : ''}
                            </Typography>
                            {slot.label && slot.type !== 'occupied' && (
                              <Typography sx={{ fontSize: '0.55rem', fontWeight: 500, color: textColor, maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.1, opacity: 0.85 }}>
                                {slot.label}
                              </Typography>
                            )}
                            {slot.type === 'occupied' && (
                              <Tooltip title="View or print this slot QR">
                                <IconButton
                                  size="small"
                                  onMouseDown={(event) => event.stopPropagation()}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedSlotQr({ title: `${currentLayoutName} ${slotId}`, value: slotQrCode });
                                  }}
                                  sx={{ position: 'absolute', top: 0, right: 0, p: 0.2, color: textColor }}
                                >
                                  <QrCodeIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Add/Edit Stock Dialog */}
      <Dialog open={stockDialogOpen} onClose={handleStockDialogClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ backgroundColor: '#00695C', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InventoryIcon />
            {selectedStock ? 'Edit Stock Batch' : 'Add New Stock Batch'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Batch Number"
                value={stockForm.batch_number}
                onChange={(e) => setStockForm({ ...stockForm, batch_number: e.target.value.toUpperCase() })}
                required
                disabled={!!selectedStock}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date In"
                type="date"
                value={stockForm.date_in}
                onChange={(e) => setStockForm({ ...stockForm, date_in: e.target.value })}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="PFP Type"
                value={stockForm.pfp_type}
                onChange={(e) =>
                  setStockForm({ ...stockForm, pfp_type: e.target.value as 'Direct' | 'Stock' | 'Digi' })
                }
                required
              >
                <MenuItem value="Direct">Direct</MenuItem>
                <MenuItem value="Stock">Stock</MenuItem>
                <MenuItem value="Digi">Digi</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Color Name"
                value={stockForm.color_name}
                onChange={(e) => setStockForm({ ...stockForm, color_name: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer"
                value={stockForm.customer}
                onChange={(e) => setStockForm({ ...stockForm, customer: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fabrication"
                value={stockForm.fabrication}
                onChange={(e) => setStockForm({ ...stockForm, fabrication: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Chip label="Trolly Items" />
              </Divider>
              {selectedStock && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You can add new trollies or remove existing ones. Note: Trollies with releases cannot be removed.
                </Alert>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Trolly Number"
                value={currentTrollyNumber}
                onChange={(e) => setCurrentTrollyNumber(e.target.value.toUpperCase())}
                placeholder="e.g., T001"
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Quantity (KG)"
                type="number"
                value={currentTrollyQuantity}
                onChange={(e) => setCurrentTrollyQuantity(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Will be converted to LBS"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Location"
                value={currentTrollyLocation}
                onChange={(e) => setCurrentTrollyLocation(e.target.value.toUpperCase())}
                placeholder="e.g., Rack A1"
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleAddTrolly}
                    sx={{ height: '56px' }}
                  >
                    Add Trolly
                  </Button>
                </Grid>

                {trollyItems.length > 0 && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Added Trollies ({trollyItems.length})
                      </Typography>
                      <Stack spacing={1}>
                        {trollyItems.map((item, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 1,
                              bgcolor: '#f5f5f5',
                              borderRadius: 1,
                            }}
                          >
                            <Typography>
                              <strong>{item.trolly_number}</strong>: {item.quantity.toFixed(2)} LBS
                              {item.location && (
                                <span style={{ color: '#666', marginLeft: '8px' }}>@ {item.location}</span>
                              )}
                            </Typography>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveTrolly(item.trolly_number)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Stack>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" fontWeight="bold" align="right">
                        Total: {getTotalWeight().toFixed(2)} LBS
                      </Typography>
                    </Paper>
                  </Grid>
                )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStockDialogClose}>Cancel</Button>
          <Button
            onClick={handleStockSubmit}
            variant="contained"
            disabled={saveLoading || !stockForm.batch_number || trollyItems.length === 0}
          >
            {saveLoading ? <CircularProgress size={24} /> : selectedStock ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Release Dialog */}
      <Dialog open={releaseDialogOpen} onClose={handleReleaseDialogClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ backgroundColor: '#00695C', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrollyIcon />
            {selectedRelease ? 'Edit Release' : 'Release Stock'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={activeBatches}
                getOptionLabel={(option) =>
                  `${option.batch_number} - ${option.color_name || 'N/A'} (${option.current_weight} LBS available)`
                }
                value={activeBatches.find((b) => b.id === releaseForm.stock_id) || null}
                onChange={(e, value) => {
                  if (value) {
                    setReleaseForm({ 
                      ...releaseForm, 
                      stock_id: value.id, 
                      batch_number: value.batch_number 
                    });
                    handleBatchSelect(value.batch_number);
                  } else {
                    setReleaseForm({ ...releaseForm, stock_id: null, batch_number: '' });
                    handleBatchSelect('');
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Batch Number" required />
                )}
                disabled={!!selectedRelease}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Release Date & Time"
                type="datetime-local"
                value={releaseForm.release_datetime}
                onChange={(e) =>
                  setReleaseForm({ ...releaseForm, release_datetime: e.target.value })
                }
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={isReturnMode ? "P Batch (Optional)" : "P Batch"}
                value={releaseForm.p_batch}
                onChange={(e) => setReleaseForm({ ...releaseForm, p_batch: e.target.value.toUpperCase() })}
                required={!isReturnMode}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                helperText={isReturnMode ? "P Batch is optional for returns" : ""}
              />
            </Grid>

            {isReturnMode && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Return Department"
                    value={returnDepartment}
                    onChange={(e) => setReturnDepartment(e.target.value)}
                    required
                  >
                    <MenuItem value="dyeing">Dyeing</MenuItem>
                    <MenuItem value="finishing">Finishing</MenuItem>
                    <MenuItem value="warehouse">Warehouse</MenuItem>
                    <MenuItem value="knitting">Knitting</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Return Reason"
                    multiline
                    rows={2}
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    required
                    placeholder="Enter reason for returning this batch..."
                  />
                </Grid>
              </>
            )}

            {!selectedRelease && releaseForm.batch_number && availableTrollies.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    No trolly items found for this batch
                  </Typography>
                  <Typography variant="body2">
                    This batch was created before the multi-trolly system or has no available trollies. 
                    To use the trolly-based release system:
                  </Typography>
                  <ul style={{ marginTop: 8, marginBottom: 0 }}>
                    <li>Delete this batch and recreate it with trolly items, or</li>
                    <li>Use the old single-quantity release method (contact admin)</li>
                  </ul>
                </Alert>
              </Grid>
            )}

            {availableTrollies.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Chip label={selectedRelease ? "Released Trollies" : "Select Trollies to Release"} />
                  </Divider>
                  {selectedRelease && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      You can modify the release quantities. The system will adjust inventory accordingly.
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Available Trollies
                    </Typography>
                    <Stack spacing={2}>
                      {availableTrollies.map((trolly) => {
                        const releaseItem = trollyReleases.find(t => t.trolly_number === trolly.trolly_number);
                        return (
                          <Box key={trolly.trolly_number}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2">
                                <strong>{trolly.trolly_number}</strong>
                              </Typography>
                              <Typography variant="body1" color="success.main" fontWeight="bold">
                                Available: {Number(trolly.current_quantity).toFixed(2)} LBS
                              </Typography>
                            </Box>
                            <TextField
                              fullWidth
                              size="small"
                              label={`Release from ${trolly.trolly_number} (LBS)`}
                              type="number"
                              value={releaseItem?.quantity || ''}
                              onChange={(e) => handleTrollyReleaseQuantityChange(trolly.trolly_number, e.target.value)}
                              inputProps={{ 
                                min: 0, 
                                max: trolly.current_quantity, 
                                step: 0.01 
                              }}
                              placeholder="Enter quantity to release"
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Button 
                      fullWidth 
                      variant="contained" 
                      color="primary"
                      onClick={() => {
                        const updatedReleases = availableTrollies.map(trolly => ({
                          trolly_number: trolly.trolly_number,
                          quantity: Number(trolly.current_quantity)
                        }));
                        setTrollyReleases(updatedReleases);
                      }}
                      sx={{ mb: 2 }}
                    >
                      Release Total
                    </Button>
                    {trollyReleases.length > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
                        <Typography variant="subtitle2">
                          Selected Trollies: {trollyReleases.length}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          Total: {getTotalReleasing().toFixed(2)} LBS
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={3}
                value={releaseForm.remarks}
                onChange={(e) => setReleaseForm({ ...releaseForm, remarks: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, alignItems: 'stretch', p: 2 }}>
          {!selectedRelease && (
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <Button
                fullWidth
                variant={isReturnMode ? "outlined" : "contained"}
                onClick={() => {
                  setIsReturnMode(false);
                  setReturnDepartment('');
                  setReturnReason('');
                }}
                disabled={!isReturnMode}
                color="primary"
              >
                Release All
              </Button>
              <Button
                fullWidth
                variant={isReturnMode ? "contained" : "outlined"}
                onClick={() => setIsReturnMode(true)}
                disabled={isReturnMode}
                color="warning"
              >
                Return Batch
              </Button>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={handleReleaseDialogClose}>Cancel</Button>
            <Button
              onClick={handleReleaseSubmit}
              variant="contained"
              disabled={
                saveLoading ||
                !releaseForm.stock_id ||
                (!isReturnMode && !releaseForm.p_batch) ||
                (isReturnMode && (!returnDepartment || !returnReason)) ||
                trollyReleases.length === 0
              }
            >
              {saveLoading ? <CircularProgress size={24} /> : selectedRelease ? 'Update' : (isReturnMode ? 'Return' : 'Release')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Stock Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Typography variant="h6">
            Stock Batch Details: {viewStock?.batch_number}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {viewStock && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Date In
                  </Typography>
                  <Typography variant="body1">
                    {new Date(viewStock.date_in).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Initial Weight
                  </Typography>
                  <Typography variant="body1">{viewStock.initial_weight} LBS</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Current Weight
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {viewStock.current_weight} LBS
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    PFP Type
                  </Typography>
                  <Chip label={viewStock.pfp_type} color={getPfpTypeColor(viewStock.pfp_type)} size="small" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    No. of Trollies
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {viewStock.trolly_count || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Color
                  </Typography>
                  <Typography variant="body1">{viewStock.color_name || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Customer
                  </Typography>
                  <Typography variant="body1">{viewStock.customer || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Status
                  </Typography>
                  <Chip label={viewStock.status} color={getStatusColor(viewStock.status)} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Fabrication
                  </Typography>
                  <Typography variant="body1">
                    {viewStock.fabrication || '-'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Trolly Items Section */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Trolly Items ({viewTrollyItems.length} trollies)
                </Typography>
                {canEdit && viewStock && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenAddTrolly(viewStock)}
                    sx={{ bgcolor: '#00695C' }}
                  >
                    Add Returned Trolly
                  </Button>
                )}
              </Box>
              
              {viewTrollyItems.length > 0 ? (
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Trolly Number</strong></TableCell>
                        <TableCell><strong>Location</strong></TableCell>
                        <TableCell align="right"><strong>Initial Qty (LBS)</strong></TableCell>
                        <TableCell align="right"><strong>Current Qty (LBS)</strong></TableCell>
                        <TableCell align="right"><strong>Released (LBS)</strong></TableCell>
                        <TableCell align="center"><strong>Status</strong></TableCell>
                        {canEdit && <TableCell align="center"><strong>Edit</strong></TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewTrollyItems.map((trolly) => {
                        const released = Number(trolly.initial_quantity) - Number(trolly.current_quantity);
                        const percentRemaining = (Number(trolly.current_quantity) / Number(trolly.initial_quantity)) * 100;
                        return (
                          <TableRow key={trolly.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {trolly.trolly_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {trolly.location || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {Number(trolly.initial_quantity).toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                color={percentRemaining < 20 ? 'error' : percentRemaining < 50 ? 'warning.main' : 'textPrimary'}
                                fontWeight="bold"
                              >
                                {Number(trolly.current_quantity).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {released.toFixed(2)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={Number(trolly.current_quantity) > 0 ? 'Active' : 'Depleted'}
                                color={Number(trolly.current_quantity) > 0 ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            {canEdit && !trolly.legacy_record && (
                              <TableCell align="center">
                                <Tooltip title="Edit trolly details">
                                  <IconButton size="small" onClick={() => handleOpenEditTrolly(trolly)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell><strong>Total</strong></TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right">
                          <strong>{viewTrollyItems.reduce((sum, t) => sum + Number(t.initial_quantity), 0).toFixed(2)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{viewTrollyItems.reduce((sum, t) => sum + Number(t.current_quantity), 0).toFixed(2)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            {viewTrollyItems.reduce((sum, t) => 
                              sum + (Number(t.initial_quantity) - Number(t.current_quantity)), 0
                            ).toFixed(2)}
                          </strong>
                        </TableCell>
                        <TableCell></TableCell>
                        {canEdit && <TableCell></TableCell>}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper sx={{ p: 2, mb: 3, textAlign: 'center', bgcolor: '#f9f9f9' }}>
                  <Typography color="textSecondary">No trolly items found for this batch</Typography>
                </Paper>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                Release History ({stockReleases.length} releases)
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>P Batch</TableCell>
                      <TableCell>Quantity (LBS)</TableCell>
                      <TableCell>Released By</TableCell>
                      <TableCell>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stockReleases.map((release) => (
                      <TableRow key={release.id}>
                        <TableCell>
                          {new Date(release.release_datetime).toLocaleString()}
                        </TableCell>
                        <TableCell>{release.p_batch || '-'}</TableCell>
                        <TableCell>
                          <Typography fontWeight="bold" color="primary">
                            {Number(release.releasing_quantity).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>{release.released_by_name || '-'}</TableCell>
                        <TableCell>{release.remarks || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {stockReleases.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="textSecondary">No releases yet</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Manage Trollies Dialog */}
      <Dialog open={manageTrollyDialogOpen} onClose={() => setManageTrollyDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ backgroundColor: '#00695C', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TuneIcon />
              Manage Trollies — {manageTrollyBatch?.batch_number}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {manageTrollyBatch && (
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {manageTrollyBatch.status} · {Number(manageTrollyBatch.current_weight).toFixed(2)} LBS remaining
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {canEdit && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => manageTrollyBatch && handleOpenAddTrolly(manageTrollyBatch)}
                sx={{ bgcolor: '#00695C' }}
              >
                Add Returned Trolly
              </Button>
            </Box>
          )}
          {manageTrollyLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : manageTrollyItems.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f9f9f9' }}>
              <Typography color="textSecondary">No trolly items found for this batch</Typography>
            </Paper>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>Trolly #</strong></TableCell>
                    <TableCell><strong>Location</strong></TableCell>
                    <TableCell align="right"><strong>Initial (LBS)</strong></TableCell>
                    <TableCell align="right"><strong>Current (LBS)</strong></TableCell>
                    <TableCell align="right"><strong>Released (LBS)</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    {canEdit && <TableCell align="center"><strong>Actions</strong></TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {manageTrollyItems.map((trolly) => {
                    const released = Number(trolly.initial_quantity) - Number(trolly.current_quantity);
                    return (
                      <TableRow key={trolly.id} hover>
                        <TableCell><Typography variant="body2" fontWeight="bold">{trolly.trolly_number}</Typography></TableCell>
                        <TableCell><Typography variant="body2" color="textSecondary">{trolly.location || '—'}</Typography></TableCell>
                        <TableCell align="right">{Number(trolly.initial_quantity).toFixed(2)}</TableCell>
                        <TableCell align="right">
                          <Typography
                            color={Number(trolly.current_quantity) <= 0 ? 'error' : Number(trolly.current_quantity) / Number(trolly.initial_quantity) < 0.2 ? 'warning.main' : 'textPrimary'}
                            fontWeight="bold" variant="body2"
                          >
                            {Number(trolly.current_quantity).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{released.toFixed(2)}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={trolly.status}
                            color={trolly.status === 'Active' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        {canEdit && (
                          <TableCell align="center">
                            <Tooltip title="Print trolley QR sheet">
                              <IconButton size="small" onClick={() => handlePrintTrolleySheet({
                                ...trolly,
                                pfp_type: manageTrollyBatch?.pfp_type,
                                color_name: manageTrollyBatch?.color_name,
                                customer: manageTrollyBatch?.customer,
                                fabrication: manageTrollyBatch?.fabrication,
                                date_in: manageTrollyBatch?.date_in,
                              })} color="secondary">
                                <PdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit trolly number / location">
                              <IconButton size="small" onClick={() => handleOpenEditTrolly(trolly)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {Number(trolly.current_quantity) > 0 && (
                              <Tooltip title="Transfer load to another trolly">
                                <IconButton size="small" onClick={() => handleOpenTransfer(trolly)} color="info">
                                  <TransferIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ bgcolor: '#f0f7f0' }}>
                    <TableCell colSpan={2}><strong>Total</strong></TableCell>
                    <TableCell align="right"><strong>{manageTrollyItems.reduce((s, t) => s + Number(t.initial_quantity), 0).toFixed(2)}</strong></TableCell>
                    <TableCell align="right"><strong>{manageTrollyItems.reduce((s, t) => s + Number(t.current_quantity), 0).toFixed(2)}</strong></TableCell>
                    <TableCell align="right"><strong>{manageTrollyItems.reduce((s, t) => s + (Number(t.initial_quantity) - Number(t.current_quantity)), 0).toFixed(2)}</strong></TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageTrollyDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedSlotQr)} onClose={() => setSelectedSlotQr(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedSlotQr?.title} QR Code</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
          {selectedSlotQr && <img src={buildQrImageUrl(selectedSlotQr.value, 500)} alt="Floor slot QR code" style={{ width: 300, height: 300, maxWidth: '100%' }} />}
          <Typography variant="body2" sx={{ wordBreak: 'break-all', textAlign: 'center' }}>{selectedSlotQr?.value}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedSlotQr(null)}>Close</Button>
          {selectedSlotQr && <Button variant="contained" startIcon={<PdfIcon />} onClick={() => handlePrintSlotQr(selectedSlotQr.value, selectedSlotQr.title)}>Print A4 QR</Button>}
        </DialogActions>
      </Dialog>

      <Dialog open={mergeDialogOpen} onClose={() => { setMergeDialogOpen(false); stopMergeScanner(); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#7B1FA2', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FloorLayoutIcon />
            Merge Trolley with Slot QR
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {mergeScanError && <Alert severity="error" sx={{ mb: 2 }}>{mergeScanError}</Alert>}
          {!window.isSecureContext && <Alert severity="warning" sx={{ mb: 2 }}>Camera scanning requires HTTPS on this device. You can enter the QR contents manually here, or open the app over HTTPS to use the camera.</Alert>}
          <Box id="merge-qr-reader" sx={{ width: '100%', minHeight: 260, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', background: '#f7f7f7', mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Trolley QR"
                value={mergeTrolleyQr}
                onChange={(e) => setMergeTrolleyQr(e.target.value)}
                placeholder="Scan trolley QR"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Floor Slot QR"
                value={mergeSlotQr}
                onChange={(e) => setMergeSlotQr(e.target.value)}
                placeholder="Scan slot QR"
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => startMergeScanner('trolley')}>
              Scan Trolley QR
            </Button>
            <Button variant="contained" color="secondary" onClick={() => startMergeScanner('slot')}>
              Scan Slot QR
            </Button>
            {mergeAvailableCameras.length > 1 && (
              <Button variant="outlined" onClick={() => {
                const nextIndex = (mergeCameraIndex + 1) % mergeAvailableCameras.length;
                setMergeCameraIndex(nextIndex);
                if (mergeScanMode) {
                  stopMergeScanner();
                  startMergeScanner(mergeScanMode);
                }
              }}>
                Switch Camera
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMergeDialogOpen(false); stopMergeScanner(); }}>Cancel</Button>
          <Button variant="contained" onClick={handleMergeQrAssignment} disabled={saveLoading || !mergeTrolleyQr.trim() || !mergeSlotQr.trim()}>
            {saveLoading ? <CircularProgress size={20} /> : 'Link Trolley'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Trolly Load Dialog */}
      <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1565C0', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TransferIcon />
            Transfer Trolly Load
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          {transferSourceItem && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f0f4ff', borderRadius: 1, border: '1px solid #90caf9' }}>
              <Typography variant="subtitle2" color="textSecondary">Source Trolly</Typography>
              <Typography variant="h6" fontWeight="bold">{transferSourceItem.trolly_number}</Typography>
              <Typography variant="body2" color="textSecondary">
                Available: <strong>{Number(transferSourceItem.current_quantity).toFixed(2)} LBS</strong>
                {transferSourceItem.location ? ` · ${transferSourceItem.location}` : ''}
              </Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Destination Trolly Number"
                value={transferForm.to_trolly_number}
                onChange={(e) => setTransferForm({ ...transferForm, to_trolly_number: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                helperText="Enter an existing trolly number in this batch, or a new trolly number to create"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2">Transfer Amount</Typography>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <input
                    type="checkbox"
                    id="transfer-all"
                    checked={transferForm.transfer_all}
                    onChange={(e) => setTransferForm({ ...transferForm, transfer_all: e.target.checked, transfer_quantity: '' })}
                  />
                  <label htmlFor="transfer-all" style={{ fontSize: 13, cursor: 'pointer' }}>Transfer All</label>
                </Box>
              </Box>
              <TextField
                fullWidth
                label="Quantity (LBS)"
                type="number"
                value={transferForm.transfer_all ? (transferSourceItem ? Number(transferSourceItem.current_quantity).toFixed(2) : '') : transferForm.transfer_quantity}
                onChange={(e) => setTransferForm({ ...transferForm, transfer_quantity: e.target.value })}
                disabled={transferForm.transfer_all}
                inputProps={{ min: 0.01, step: 0.01, max: transferSourceItem ? Number(transferSourceItem.current_quantity) : undefined }}
                helperText={transferSourceItem ? `Max: ${Number(transferSourceItem.current_quantity).toFixed(2)} LBS` : ''}
              />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }} icon={<TransferIcon />}>
            If the destination trolly does not exist, it will be created automatically. The batch total weight will not change — this is a redistribution.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTransferSubmit}
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={16} /> : <TransferIcon />}
            sx={{ bgcolor: '#1565C0' }}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Returned Trolly Dialog */}
      <Dialog open={addTrollyDialogOpen} onClose={() => setAddTrollyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#00695C', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            Add Returned Trolly — Batch {addTrollyBatch?.batch_number}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          {addTrollyBatch?.status === 'Depleted' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This batch is currently <strong>Depleted</strong>. Adding a trolly will reactivate it automatically.
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Trolly Number"
                value={addTrollyForm.trolly_number}
                onChange={(e) => setAddTrollyForm({ ...addTrollyForm, trolly_number: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                required
                helperText="If this trolly already exists in the batch, quantity will be added to it"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity (KG)"
                type="number"
                value={addTrollyForm.quantity_kg}
                onChange={(e) => setAddTrollyForm({ ...addTrollyForm, quantity_kg: e.target.value })}
                inputProps={{ min: 0.01, step: 0.01 }}
                required
                helperText={addTrollyForm.quantity_kg && parseFloat(addTrollyForm.quantity_kg) > 0
                  ? `= ${(parseFloat(addTrollyForm.quantity_kg) * 2.20462).toFixed(2)} LBS`
                  : 'Will be converted to LBS'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location (optional)"
                value={addTrollyForm.location}
                onChange={(e) => setAddTrollyForm({ ...addTrollyForm, location: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                placeholder="e.g., Rack A1"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks (optional)"
                multiline
                rows={2}
                value={addTrollyForm.remarks}
                onChange={(e) => setAddTrollyForm({ ...addTrollyForm, remarks: e.target.value })}
                placeholder="Reason for adding back, correction details..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTrollyDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddTrollySubmit}
            variant="contained"
            disabled={saveLoading || !addTrollyForm.trolly_number.trim() || !addTrollyForm.quantity_kg || parseFloat(addTrollyForm.quantity_kg) <= 0}
            sx={{ bgcolor: '#00695C' }}
          >
            {saveLoading ? <CircularProgress size={24} /> : 'Add Trolly'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Trolly Item Dialog */}
      <Dialog open={editTrollyDialogOpen} onClose={() => setEditTrollyDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#00695C', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            Edit Trolly — {editTrollyItem?.trolly_number}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Alert severity="info" sx={{ mb: 2 }}>
            Only the trolly number and location can be edited here. Use <strong>Add Returned Trolly</strong> to restore quantity.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trolly Number"
                value={editTrollyForm.trolly_number}
                onChange={(e) => setEditTrollyForm({ ...editTrollyForm, trolly_number: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={editTrollyForm.location}
                onChange={(e) => setEditTrollyForm({ ...editTrollyForm, location: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                placeholder="e.g., Rack A1"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTrollyDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditTrollySubmit}
            variant="contained"
            disabled={saveLoading || !editTrollyForm.trolly_number.trim()}
          >
            {saveLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={csvUploadDialogOpen} onClose={handleCsvDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#00695C', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon />
            Update Stock Data from CSV
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}
          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              CSV processed successfully!
            </Alert>
          )}
          
          {!csvUploadResult ? (
            <>
              <Typography variant="body2" color="textSecondary" paragraph>
                Upload a CSV file with batch numbers and update fabrication, color, and customer information. The system will update existing batches.
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                <strong>CSV Format:</strong> (All columns except batch_number are optional)
              </Typography>
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mb: 2 }}>
                <Typography variant="caption" fontFamily="monospace">
                  batch_number,fabrication,color_name,customer<br />
                  PAB294242,"Fabrication details here","Blue","ABC Company"<br />
                  1337292,"Another fabrication","Red","XYZ Corp"<br />
                  <br />
                  <em>Note: You can include only the columns you want to update</em>
                </Typography>
              </Paper>
              
              {/* Excel Integration Section */}
              <Box sx={{ mb: 3, p: 2, border: '1px dashed #00695C', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#00695C', fontWeight: 'bold' }}>
                  Excel Integration
                </Typography>
                <Stack spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCopyBatchNumbers}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    📋 Copy All Batch Numbers
                  </Button>
                  <Typography variant="caption" color="textSecondary">
                    Step 1: Copy batch numbers → Paste in Excel Column A
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Step 2: Fill fabrication, color, customer in columns B, C, D
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Step 3: Select & copy all data → Paste below → Click "Parse & Upload"
                  </Typography>
                  <TextField
                    multiline
                    rows={4}
                    placeholder="Paste data from Excel here&#10;Example:&#10;PAB5020045[TAB]micro modal 30/4 sj[TAB]black[TAB]CK&#10;PAB5020002[TAB]big flank 56[TAB]red[TAB]Dell"
                    value={excelPasteData}
                    onChange={(e) => setExcelPasteData(e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleParseExcelData}
                    disabled={!excelPasteData.trim() || csvUploadLoading}
                    startIcon={csvUploadLoading ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ bgcolor: '#00695C' }}
                  >
                    {csvUploadLoading ? 'Processing...' : 'Parse & Upload Data'}
                  </Button>
                </Stack>
              </Box>
              
              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" color="textSecondary">OR</Typography>
              </Divider>
              
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2 }}
              >
                {csvFile ? csvFile.name : 'Select CSV File'}
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setCsvFile(e.target.files[0]);
                      setSaveError(null);
                    }
                  }}
                />
              </Button>
            </>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                CSV processed successfully!
              </Alert>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {csvUploadResult.summary.updated}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Updated
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {csvUploadResult.summary.notFound}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Not Found
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="error">
                      {csvUploadResult.summary.errors}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Errors
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4">
                      {csvUploadResult.summary.total}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Rows
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              {csvUploadResult.details.notFound.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Batches Not Found:
                  </Typography>
                  <Paper sx={{ p: 1, bgcolor: '#fff3cd', mb: 2, maxHeight: 100, overflow: 'auto' }}>
                    <Typography variant="caption">
                      {csvUploadResult.details.notFound.join(', ')}
                    </Typography>
                  </Paper>
                </>
              )}
              {csvUploadResult.details.errors.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Errors:
                  </Typography>
                  <Paper sx={{ p: 1, bgcolor: '#f8d7da', mb: 2, maxHeight: 100, overflow: 'auto' }}>
                    {csvUploadResult.details.errors.map((err: any, idx: number) => (
                      <Typography key={idx} variant="caption" display="block">
                        Row {err.row}: {err.error} {err.batch_number ? `(${err.batch_number})` : ''}
                      </Typography>
                    ))}
                  </Paper>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCsvDialogClose}>
            {csvUploadResult ? 'Close' : 'Cancel'}
          </Button>
          {!csvUploadResult && (
            <Button
              onClick={handleCsvUpload}
              variant="contained"
              disabled={csvUploadLoading || !csvFile}
              startIcon={csvUploadLoading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            >
              Upload
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ══ Floor Slot Edit Dialog ══ */}
      <Dialog open={slotDialogOpen} onClose={() => setSlotDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {selectedSlots.size > 1
            ? `Edit ${selectedSlots.size} Slots`
            : selectedFloorSlot
              ? `Edit Slot ${getRowLabel(selectedFloorSlot.row)}${selectedFloorSlot.col + 1}`
              : 'Edit Slot'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              label="Slot Type"
              value={slotEditForm.type}
              onChange={(e) => setSlotEditForm({ ...slotEditForm, type: e.target.value as FloorSlot['type'] })}
              fullWidth
              size="small"
            >
              <MenuItem value="empty">Empty / Available</MenuItem>
              <MenuItem value="occupied">Occupied (Trolly)</MenuItem>
              <MenuItem value="path">Floor Path</MenuItem>
              <MenuItem value="boundary">Boundary</MenuItem>
              <MenuItem value="blocked">Blocked / Non-usable</MenuItem>
            </TextField>

            <TextField
              label="Slot Name (optional)"
              value={slotEditForm.label}
              onChange={(e) => setSlotEditForm({ ...slotEditForm, label: e.target.value })}
              fullWidth
              size="small"
              placeholder="e.g. Slot A1 / Trolley Bay 12"
            />

            {slotEditForm.type === 'occupied' && (
              <>
                <TextField
                  label="Slot No."
                  value={slotEditForm.trollyNumber}
                  onChange={(e) => setSlotEditForm({ ...slotEditForm, trollyNumber: e.target.value.toUpperCase() })}
                  fullWidth size="small"
                  placeholder="e.g. RACK-A1"
                  helperText="Must match the trolly's Location field in Stock Management to auto-link on hover"
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                />
                <TextField
                  label="Batch Number (optional)"
                  value={slotEditForm.batchNumber}
                  onChange={(e) => setSlotEditForm({ ...slotEditForm, batchNumber: e.target.value })}
                  fullWidth size="small"
                />
                <Box>
                  <Typography variant="body2" mb={1} color="text.secondary">Colour</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {SLOT_COLORS.map(c => (
                      <Box
                        key={c}
                        onClick={() => setSlotEditForm({ ...slotEditForm, color: c })}
                        sx={{
                          width: 28, height: 28, bgcolor: c, borderRadius: '50%', cursor: 'pointer',
                          border: slotEditForm.color === c ? '3px solid #212121' : '2px solid transparent',
                          transition: 'transform 0.12s',
                          '&:hover': { transform: 'scale(1.25)' },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {(slotEditForm.type === 'empty' || slotEditForm.type === 'blocked' || slotEditForm.type === 'path' || slotEditForm.type === 'boundary') && (
              <Typography variant="caption" color="text.secondary">
                Tip: double-click any cell in edit mode to open this slot details editor quickly.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSlotDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSlotSave} variant="contained">Save Slot</Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button (SpeedDial) */}
      {(canEdit || canRelease) && (
        <SpeedDial
          ariaLabel="PFP Trolly Actions"
          sx={{
            position: 'fixed',
            bottom: { xs: 16, md: 24 },
            right: { xs: 16, md: 24 },
          }}
          icon={<SpeedDialIcon icon={<TrollyIcon />} />}
        >
          {canEdit && (
            <SpeedDialAction
              icon={<AddIcon />}
              tooltipTitle="Add Stock"
              onClick={() => handleStockDialogOpen()}
            />
          )}
          {canEdit && (
            <SpeedDialAction
              icon={<CloudUploadIcon />}
              tooltipTitle="Update Stock Data CSV"
              onClick={() => setCsvUploadDialogOpen(true)}
            />
          )}
          {canEdit && (
            <SpeedDialAction
              icon={<QrCodeIcon />}
              tooltipTitle="Merge Trolley to Floor Slot"
              onClick={openMergeDialog}
            />
          )}
          {canRelease && (
            <SpeedDialAction
              icon={<RemoveIcon />}
              tooltipTitle="Release Stock"
              onClick={() => handleReleaseDialogOpen()}
            />
          )}
        </SpeedDial>
      )}
    </Box>
  );
};

export default PfpTrollyManagement;
